// TinyDraft — MagicaVoxel-style WebXR voxel editor.
//
// Voxel-grid-rewrite scaffold. Owns the scene/renderer/camera, the work-
// volume cube, VR session management, the voxel grid + mesher, and the
// per-controller pick → draw/erase loop. Remaining phases: extrude →
// phase 3, move/marquee → 4, dials/toolkit/etc. → 5, history/save → 6.

import "./app/telemetry"; // FIRST: error telemetry (inert without a DSN) so boot crashes get captured

import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

import { Orbit3D } from "./view/orbit3d";
import { installLighting } from "./view/lighting";
import { installWorkVolumeView } from "./scene/work-volume-view";
import { installReference } from "./scene/reference";
import { ground, groundMat } from "./scene/ground-grid";
import { ROOM_TARGET, installSceneObjects } from "./model/scene-objects";
import {
  currentMirrorConfig,
  mirrorActiveNow,
  mirrorExpandSelection,
} from "./core/edit-core/mirror-config";
import { installAxisSnapOrbit } from "./app/axis-snap-orbit";
import {
  MODEL_AXES,
  PLAN_AXES,
  nearestAxisIn,
  planUpForAxis,
  snapUpForAxis,
} from "./view/axis-math";
import {
  DESKTOP_GRID_HALF_SIZE,
  VOLUME_HEIGHT,
  applyResizeExtent,
  applyVolumeBounds,
  cellBounds,
  initWorkVolumeBounds,
  recenterCanvasBounds,
  recomputeCellBounds,
  resampleGridToBounds,
  volumeBounds,
} from "./scene/work-volume-bounds";
import { CELL_SIZE, type ChunkStorage, type VoxelGrid } from "./model/voxel-grid";
import { type PaletteLookup } from "./view/voxel-mesh";
import {
  type ControllerRay,
  createControllerRay,
  setControllerRayTint,
} from "./view/controller-ray";
import {
  type CellBounds,
  type CellBox,
  type PickInput,
  type PickTarget,
  pickRay,
} from "./interaction/pick";
import { type VolumeBounds } from "./scene/work-volume";
import {
  controllerEffectiveWorldPos,
  controllerModelFactory,
  controllerStates,
  controllers,
  findControllerIndexByHandedness,
  grips,
  laserLocalDir,
  pulseHaptic,
  setPrecisionAim,
} from "./input/controllers";
import {
  desktopMouse,
  installDesktopMarqueeRect,
  installDesktopMouse,
} from "./input/desktop";
import {
  PALETTE_SIZE,
  applyPaletteSnapshot,
  paletteColor,
  paletteColorCss,
  paletteColorHex,
  paletteColorInto,
  parseColorInt,
  snapshotPalette,
} from "./ui/palette";
import {
  armEyedropper,
  disarmEyedropper,
  deliverEyedrop,
  eyedropperArmed,
} from "./app/eyedropper";
import {
  type DesktopTool,
  type DesktopUIState,
  installDesktopUI,
} from "./ui/desktop-ui";
import {
  palettePanelMesh,
} from "./ui/palette-panel";
import {
  toolkitPanelMesh,
} from "./ui/toolkit-panel";
import {
  settingsPanelMesh,
} from "./ui/settings-panel";
import {
  helpPanelMesh,
} from "./ui/help-panel";
import { onState, setState, uiState } from "./ui/state";
import { loadSettings, saveSetting } from "./ui/settings-store";
import {
  handleCanvasDrag,
  handleNavigation,
} from "./interaction/handlers/navigation";
import {
  extrudeDraftBox,
  extrudeEraseIncludeBox,
  handleExtrudeDrag,
  setExtrudeLiveDraw,
  setExtrudeSnapshotCapture,
} from "./interaction/handlers/extrude";
import {
  handleBoxDrag,
  setBoxLiveDraw,
  setBoxSnapshotCapture,
} from "./interaction/handlers/box";
import {
  handleCircleDrag,
  setCircleSnapshotCapture,
} from "./interaction/handlers/circle";
import { handleVoxelPaintDrag } from "./interaction/handlers/voxel-paint";
import {
  cancelFreeformPoly,
  closeFreeformPoly,
  freeformPolyActive,
  popFreeformVertex,
} from "./interaction/handlers/freeform";
import { setMirror } from "./interaction/mirror";
import { handleMarqueeDrag } from "./interaction/handlers/marquee";
import { handleMoveDrag } from "./interaction/handlers/move";
import { handleResizeDrag } from "./interaction/handlers/resize";
import { interaction, setInteraction } from "./interaction/state";
import {
  grabBarGroup,
  grabBarMesh,
} from "./ui/grab-bar";
import {
  type ActionMode,
  type Handedness,
  type SelectAction,
  type ShapeMode,
  type TopMode,
} from "./interaction/tool";
import { installClipboard } from "./core/edit-core/clipboard";
import { grid } from "./core/edit-core/grid";
import {
  clearCanvas,
  fillCanvas,
  setCanvasSnapshotCapture,
  flipSelection,
  recolorSwatch,
  resetSwatches,
  rotateSelection,
} from "./core/edit-core/canvas-ops";
import {
  clearSelFloat,
  deleteSelection,
  getSelFloat,
  selection,
  selectionStateString,
} from "./core/edit-core/selection";
import {
  type LengthUnit,
  M_TO_IN,
  formatLengthIn,
  getDisplayUnit,
  getInchesPerCell,
  setDisplayUnit,
  setVoxelScale,
} from "./model/units";
import {
  getModel,
  listModelIds,
  listModels,
  newId,
  putModel,
  readModelFile,
} from "./model/collection-store";
import { putBake } from "./model/bake-store";
import {
  type BakedAssetCore,
  type SavedModel,
  cellsToGrid,
  flattenLegacyVoxels,
  gridToVoxels,
  isV2,
  v1CellsToVoxels,
  voxelsToGrid,
} from "./model/saved-model";
import { Thumbnailer } from "./view/thumbnailer";
import {
  type DimensionsInitial,
  type DimensionsResult,
  installDimensionsPanel,
} from "./ui/dimensions-panel";
import { type CollectionDock, installCollectionDock } from "./ui/collection-dock";
import { createSaveSession } from "./ui/save-session";
import { installFloorMode } from "./floor/floor-mode";
import { setMeasurementsPinned } from "./floor/core";
import { commitFloor } from "./floor/history";
import { installVRInput, type PanelHit } from "./app/vr-input";
import { installDesktopInput } from "./app/desktop-input";
import { installCameraMove } from "./app/camera-move";
import { type AppContext } from "./app/context";
import { createFrameLoop } from "./app/frame-loop";
import { createKeymap } from "./app/keymap";
import { createLifecycle } from "./app/lifecycle";
import { createToolRegistry } from "./app/tool-registry";
import { installMoveTool } from "./tools/move";
import { installResizeTool } from "./tools/resize";
import { installSelectTool } from "./tools/select";
import { installSketchTool } from "./tools/sketch";
import { installPaintTool } from "./tools/paint";
import { installBucketTool } from "./tools/bucket";
import { createSectionRegistry } from "./ui/section-registry";
import { createVisibilityRegistry } from "./ui/visibility-registry";
import { clearClones, cloneForModel, ensureAsset } from "./floor/bake-cache";
import { beginBusy } from "./ui/busy";
import { type SavedFloorPlan } from "./floor/saved-floor-plan";
import { listPlans, putPlan } from "./floor/floor-plan-store";
import { pickBootLayout } from "./floor/starter-scene";
import { installFloorUI } from "./ui/floor-ui";
import { SelectionOutline } from "./view/selection-outline";
import { BoxPreview } from "./view/box-preview";
import { VoxelRaymarchView } from "./view/voxel-raymarch";
import { MarqueeRect } from "./view/marquee-rect";
import {
  beginTransaction,
  clearHistory,
  commitSelectionChange,
  endTransaction,
  historyDepth,
  onPaletteApply,
  onSnapshotApply,
  onTransactionCommit,
  recordSnapshot,
  redo,
  suppressRecording,
  undo,
} from "./model/history";
import {
  renderGizmoOverlay,
  setGizmoHiddenAxis,
  setGizmoHover,
} from "./ui/gizmo";
import { showAlert } from "./ui/modal";
import {
  haltBoot,
  installContextLossRecovery,
  installSmallScreenNotice,
  showWebGLFallback,
  webgl2Available,
} from "./app/boot-guards";
import { installAccountMenu } from "./ui/auth-panel";
import { syncController } from "./sync/controller";
import { onScopeChange } from "./sync/account-scope";
import { authSettled, hasPersistedSession, setScopeSwitchGuard } from "./auth/session";
import { type ShortcutSection, showSettings } from "./ui/settings-overlay";
import { installDesktopHelp } from "./ui/desktop-help";
import { installWelcome } from "./ui/welcome";
import { actionForKey } from "./ui/hotkeys";
import {
  actionDial,
  selectActionDial,
  shapeDial,
} from "./ui/dials";

// ── Renderer / Scene / Camera ────────────────────────────────────────────

// alpha:true so the framebuffer can be transparent in AR sessions —
// otherwise the renderer's clear color composites over passthrough.
// Guarded: without working WebGL2 the constructor throws and the user would
// get a silent blank screen — show a static fallback and halt boot instead
// (main.ts is all eval-time code; haltBoot throws to stop the rest of it).
const renderer = ((): THREE.WebGLRenderer => {
  try {
    if (!webgl2Available()) throw new Error("WebGL2 context unavailable");
    return new THREE.WebGLRenderer({ antialias: true, alpha: true });
  } catch (err) {
    showWebGLFallback();
    haltBoot(err instanceof Error ? err.message : String(err));
  }
})();
renderer.setSize(window.innerWidth, window.innerHeight);
// Cap at 2: retina/5K displays report DPR up to 3 (4-9x the pixels, with MSAA
// on top) for no visible gain at this art style; 2 is the standard ceiling.
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setClearColor(0x000000, 0);
renderer.xr.enabled = true;
// Shadows exist only in the presentation lighting rig (the sun in
// floor-scene.ts). Enabled up front — flipping this at runtime would need a
// manual recompile of every material; with no shadow-casting light visible
// (the editing rig), the shadow pass and the shader cost are both skipped.
renderer.shadowMap.enabled = true;
// PCF (not PCFSoft): soft shadows are ~9 taps per lit fragment — too rich
// for the Quest's fragment budget in the walkthrough. PCF reads slightly
// crunchier shadow edges on desktop; acceptable.
renderer.shadowMap.type = THREE.PCFShadowMap;

// Idle frame gating (desktop only). Nothing in the scene animates without
// input — the view tweens finish in ~7 frames, there are no time-driven
// shaders, and the cameras have no damping — so after a quiet window the
// render loop early-outs before update + render and the canvas keeps its
// last frame. Any input wakes a fresh window; scene changes that DON'T
// arrive through input (async file/model loads, VR session end) call
// wakeRendering() explicitly. Immersive XR always renders — headsets need
// a fresh frame per vsync. (Declared up here: eval-time code below calls
// syncToolbars, which wakes.)
const IDLE_RENDER_WINDOW_MS = 3000;
let _lastActivity = performance.now();
function wakeRendering(): void {
  _lastActivity = performance.now();
}
for (const t of ["pointermove", "pointerdown", "pointerup", "wheel", "keydown", "keyup"]) {
  window.addEventListener(t, wakeRendering, { capture: true, passive: true });
}
window.addEventListener("resize", wakeRendering);
// Floor-relative tracking: y=0 is the real floor. Everything placed at y=0 (the
// layout walkthrough's floor, the model-on-a-table height) then lines up with the
// physical floor instead of drifting to the headset's start height. Set
// explicitly rather than trusting the WebXR default.
renderer.xr.setReferenceSpaceType("local-floor");
renderer.xr.setFoveation(0);
renderer.xr.setFramebufferScaleFactor(2.0);
document.body.appendChild(renderer.domElement);
// GPU resets (driver crash, OS sleep) blank the canvas: reclaim the context
// and wake the idle-gated loop on restore; suggest a reload if it never comes.
installContextLossRecovery(renderer.domElement, wakeRendering);
// Coarse-pointer + small-viewport devices get a one-time dismissible notice —
// the editor has no touch camera controls or responsive layout.
installSmallScreenNotice();

const DESKTOP_BACKGROUND = new THREE.Color(0x1a1a2e);
const scene = new THREE.Scene();
scene.background = DESKTOP_BACKGROUND;

const camera = new THREE.PerspectiveCamera(
  70,
  window.innerWidth / window.innerHeight,
  // near: 0.05 (half a cell). A tiny near plane wrecks depth-buffer precision —
  // at 0.01 vs the 500 far plane (1:50000) the LSB is huge out where the model
  // sits, so the coplanar grid-line/face bias resolves inconsistently and the
  // gridlines break up ("patchy"). 0.05 is still closer than you can practically
  // zoom (orbit minDistance 0.1) yet gives ~5× better precision.
  0.05,
  500, // far: covers the floor 3D zoom-out (orbit maxDistance 120 + the dot field)
);
const DESKTOP_CAMERA_POSITION = new THREE.Vector3(0, 20, 0.1);
camera.position.copy(DESKTOP_CAMERA_POSITION);
camera.lookAt(0, 0, 0);

// The desktop toolbar is a fixed left sidebar this many px wide. Shift the
// camera projection right by half of it so the work volume reads as centered
// in the WORKABLE area (right of the sidebar), not the whole window. Using
// setViewOffset shifts the render AND mouse picking (project/unproject)
// together, so clicks stay accurate and the orbit pivot stays on the volume.
const DESKTOP_SIDEBAR_W = 192; // keep in sync with #dt-ui width in desktop-ui.ts
function applyDesktopViewOffset(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  camera.setViewOffset(w, h, -DESKTOP_SIDEBAR_W / 2, 0, w, h);
}
applyDesktopViewOffset();

// Top-level group that navigation transforms.
const world = new THREE.Group();
scene.add(world);

// Immersive skybox: huge inverted sphere behind everything to cover WebXR
// passthrough when the user wants opaque VR. scene.background = Color
// isn't reliable in alpha-blend AR (the layer's blend mode wins).
const immersiveSkybox = new THREE.Mesh(
  new THREE.SphereGeometry(50, 32, 16),
  new THREE.MeshBasicMaterial({
    color: DESKTOP_BACKGROUND,
    side: THREE.BackSide,
    depthWrite: false,
    toneMapped: false, // must match the desktop clear color exactly, even under ACES
  }),
);
immersiveSkybox.renderOrder = -1;
immersiveSkybox.visible = false;
scene.add(immersiveSkybox);

// ── Lighting (extracted to src/view/lighting.ts) ─────────────────────────
// Two rigs (flat EDITING vs image-based PRESENTATION) + the toggle that swaps
// them; subsystems built later hook in via onLightingRig (see floorScene below).
const { onLightingRig } = installLighting({ scene, renderer, wakeRendering });

// ── Ground Grid (shader) ─────────────────────────────────────────────────

const gridGroup = new THREE.Group();

// The ground/grid shader material + floor plane live in scene/ground-grid.
gridGroup.add(ground);

const DESKTOP_GRID_POSITION = new THREE.Vector3(0, 0, 0);
const DESKTOP_GRID_ROTATION_X = 0;
// y is the work-volume floor height (metres above the physical floor —
// the session uses a local-floor reference). The volume is ~0.6m tall in
// VR, so y=0.6 puts its centre ~0.9m up, and z=-0.95 sits it just under a
// metre in front of the user.
const VR_GRID_POSITION = new THREE.Vector3(0, 0.6, -0.95);
const VR_GRID_ROTATION_X = 0;
// VR maps the volume's longer horizontal axis to ~2ft so the user can
// reach the whole grid at arm's length. Uses the INITIAL size so the
// physical size of a cell stays fixed as the canvas is resized.
function vrGridScale(): number {
  const longer = Math.max(DESKTOP_GRID_HALF_SIZE.x, DESKTOP_GRID_HALF_SIZE.y);
  return 0.6096 / (longer * 2);
}

gridGroup.position.copy(DESKTOP_GRID_POSITION);
gridGroup.rotation.x = DESKTOP_GRID_ROTATION_X;
world.add(gridGroup);

// Resize mode: per-controller index of the wall handle under the laser
// (-1 = none) and the world distance to it, captured for the gesture. Declared
// here (above the work-volume install that reads the live array) — written by
// the VR / desktop input pick loops.
const resizeHotHandle: number[] = [-1, -1];
const resizeHotDist: number[] = [0, 0];

// Assigned once the desktop UI is installed (below). Lets dimension changes
// (resize, set-dimensions, load) refresh the sidebar's size readout. Null-safe
// so setup-time calls before install are no-ops.
let desktopUIRef: { render(): void; refreshSwatches(): void } | null = null;

// Reference picture (Model-mode overlay) — owns the instance, its session
// state, and its model-timeline transform history. (scene/reference.ts)
const _refNormalTmp = new THREE.Vector3(); // import: camera→volume direction scratch
const {
  referenceImage,
  restoreReference,
  commitReference,
  clearReferenceImage,
  editingReference,
  serialize: serializeReference,
  importLoaded: importReferenceLoaded,
  toggleReferenceEdit,
  toggleReferenceHidden,
  setReferenceOpacity,
  referenceOn,
  referenceEditingOn,
  referenceHiddenOn,
  referenceOpacityValue,
  updateVisibility: updateReferenceVisibility,
} = installReference({
  gridGroup,
  onChange: () => desktopUIRef?.render(),
  wakeRendering,
});

// ── Work Volume (furniture extracted to scene/work-volume-view.ts) ───────
// The walls/ceiling grid planes, the wireframe cage, and the resize grab
// handles + their per-frame visibility/visuals. Reads the live volumeBounds;
// the bounds MUTATION (applyVolumeBounds etc.) stays below in main.
const {
  workVolumeSurfaces,
  resizeHandleMeshes,
  cage,
  rebuildWorkVolumeWalls,
  grabbedHandleIndex,
  updateResizeHandlesVisual,
  updateWorkVolumeVisibility,
} = installWorkVolumeView({
  gridGroup,
  groundMat,
  ground,
  volumeBounds,
  resizeHotHandle,
  camera,
  renderer,
  orbitTarget: () => orbitControls.target,
  isModelOrthographic: () => modelOrthographic,
});

// Work-volume BOUNDS (volumeBounds + cellBounds + the resize/prune/recenter API)
// live in scene/work-volume-bounds; wire the outward effects now that the wall
// meshes exist.
initWorkVolumeBounds({
  rebuildWorkVolumeWalls,
  reanchorReference: (b) => referenceImage.reanchor(b),
  onResized: () => {
    canvasRealIn = null;
    desktopUIRef?.render();
  },
});

// Grab bar static geometry lives in src/ui/grab-bar.ts; parent it here
// (it tracks the cube via gridGroup) and updateGrabBar below drives its
// position, scale, and orientation per frame.
gridGroup.add(grabBarGroup);
// The grab bar is a VR-only affordance (drag the canvas with the controller).
// Desktop uses orbit/pan, so hide it; toggled on/off with the XR session.
grabBarGroup.visible = false;

// ── Voxel Grid + Mesher ──────────────────────────────────────────────────
// The primary grid + selection instances are owned by core/edit-core (imported
// above) — slices import them from there, not through main.

const palette: PaletteLookup = {
  color: paletteColor,
};

// The multi-object scene model (active object + room-object resolution +
// pick→object routing state) lives in model/scene-objects.
const {
  activeObject,
  sceneObjects,
  lastPickObject,
  surfaceFallbackGrid,
  objectById,
  gestureObject,
  gestureActive,
  setGestureObject,
  isRoomAligned,
  findRoomObject,
  getOrCreateRoomObject,
  setAllGridVisible,
  setAllEdgesVisible,
  setAllLineFade,
} = installSceneObjects({ palette, gridGroup, onMeshApplied: wakeRendering });

// GPU raymarch view (#3) is the DEFAULT renderer for desktop Model-mode editing
// (an edit is a texture re-upload, not a re-mesh — what keeps a huge canvas
// responsive). The mesh path stays the fallback for VR (no stereo yet), Layout
// (placements are baked separately), and presentation lighting. ?mesh forces the
// old mesh path everywhere. The render loop swaps between them per frame (see
// `useRaymarch`), resyncing whichever renderer just became active.
const raymarchView: VoxelRaymarchView | null =
  new URLSearchParams(window.location.search).has("mesh")
    ? null
    : new VoxelRaymarchView(palette);
if (raymarchView) gridGroup.add(raymarchView.mesh);
// Renderer scheduling for desktop Model mode (decided per frame in the render
// loop): raymarch while voxels are SMALL on screen, the mesh path once zoomed
// in close. Raymarch cost is per covered pixel — cheap when the model is a
// patch of the canvas, over the frame budget when it fills a Retina screen
// (measured 17.6ms vs the mesh path's 7.3ms for the same full-screen view at
// 6016×2506; an 85Hz display budgets 11.8ms) — and a blown budget queues GPU
// frames, which reads as the hover cursor lagging exactly when zoomed in.
// Close-zoom edits are small local brushes the incremental mesher absorbs
// instantly, while big-region edits (raymarch's strength) happen zoomed out,
// so apparent cell size is the right scheduling signal. Hysteresis so orbiting
// at the boundary doesn't flip-flop. ?raymarch forces the raymarcher at every
// zoom (the perf A/B mirror of ?mesh).
let raymarchZoomedOut = true;
const RAYMARCH_MESH_AT_CELL_PX = 24; // a voxel spanning more px swaps to mesh
const RAYMARCH_RESUME_CELL_PX = 18; // and back once cells shrink under this
const FORCE_RAYMARCH = new URLSearchParams(window.location.search).has("raymarch");
const _rmCamLocalTmp = new THREE.Vector3();
const _rmClampTmp = new THREE.Vector3();
// Last frame's mesher-catch-up state, read by the swap gate above the sync.
let meshCatchupPending = false;

// Palette panel — parented to the scene so it's independent of the
// cube's transform. Visibility + positioning are driven by the A-button
// poll in the render loop.
scene.add(palettePanelMesh);
scene.add(toolkitPanelMesh);
scene.add(settingsPanelMesh);
scene.add(helpPanelMesh);

// Selection visualization. The selection itself (instance, serialization,
// history wiring, selFloat) is owned by core/edit-core/selection; the outline
// mesh is a derived view that syncs on version-bump.
const selectionOutline = new SelectionOutline();
gridGroup.add(selectionOutline.object3D);

// Box-draw preview shell — shown only while a box_draw gesture drags; the real
// voxels are filled on release (see commitBoxDraw). Parented to gridGroup so its
// cell coords align with the voxel meshes.
const boxPreview = new BoxPreview();
gridGroup.add(boxPreview.object3D);
// Preview "shield": a region shown as a solid shell over a mesh that's still
// rebuilding, so a big edit looks instantly done and its chunk-by-chunk build is
// hidden. Set by a live box_draw gesture and by a just-committed Fill; held
// until the real mesh finishes the time-sliced rebuild.
let fillShield:
  | { a: { ix: number; iy: number; iz: number }; b: { ix: number; iy: number; iz: number }; color: THREE.Color }
  | null = null;
// The one shield instance, written in place — the box_draw/extrude paths
// update it every frame of the drag, so no per-frame object literals here.
// Only the frame loop reads fillShield, so sharing the instance can't alias.
const _fillShieldBox = {
  a: { ix: 0, iy: 0, iz: 0 },
  b: { ix: 0, iy: 0, iz: 0 },
  color: new THREE.Color(),
};
function setFillShield(
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  palette: number,
): void {
  _fillShieldBox.a.ix = ax;
  _fillShieldBox.a.iy = ay;
  _fillShieldBox.a.iz = az;
  _fillShieldBox.b.ix = bx;
  _fillShieldBox.b.iy = by;
  _fillShieldBox.b.iz = bz;
  paletteColorInto(palette, _fillShieldBox.color);
  fillShield = _fillShieldBox;
}
// While the shield is up over a building mesh, the REAL mesh's grid/edge lines
// are suppressed so they don't double the shell's lines (a dark grid sweeping
// across the region). Restored to the user's toggles once meshed.
let realLinesSuppressed = false;

// Show the fill shield over the whole work volume — called right after a Fill so
// its mesh rebuild is hidden behind a solid preview instead of stripping in.
function triggerFillShield(): void {
  const x0 = Math.round(volumeBounds.minX / CELL_SIZE);
  const x1 = Math.round(volumeBounds.maxX / CELL_SIZE);
  const z0 = Math.round(volumeBounds.minZ / CELL_SIZE);
  const z1 = Math.round(volumeBounds.maxZ / CELL_SIZE);
  const y1 = Math.round(volumeBounds.height / CELL_SIZE);
  if (x1 <= x0 || y1 <= 0 || z1 <= z0) return;
  setFillShield(x0, 0, z0, x1 - 1, y1 - 1, z1 - 1, uiState.currentPaletteIndex);
  wakeRendering();
}
// Per-frame milliseconds any one mesher spends rebuilding dirty chunks. Small
// live edits finish in one frame; a volume-sized fill spreads over a few frames
// so the main thread never stalls, while a Clear (near-free teardowns) drains
// almost entirely in one. ~12ms leaves headroom in a 16ms frame for render.
const MESH_MS_BUDGET = 12;

// Marquee rect preview — only visible while a marquee_select gesture
// is in flight. Parented to scene because the marquee plane lives in
// world space (it doesn't follow gridGroup navigation).
const marqueeRect = new MarqueeRect();
scene.add(marqueeRect.object3D);

// Joystick dials — both visually share one position in front of the
// user; pollDials toggles visibility based on which stick is deflected.
// Painted once at startup so they render correctly on first summon.
scene.add(actionDial.group);
scene.add(shapeDial.group);
scene.add(selectActionDial.group);

// Both controllers reflect the current action mode (set via the
// left-joystick dial). Cursor tint and trigger behavior come from
// uiState.actionMode + uiState.shapeMode, not the controller index.


// ── VR Controllers ───────────────────────────────────────────────────────
// Controllers are set up here so the user sees their hands in VR. Per-
// controller pointing/picking is phase-2 work and will be added to this
// loop then.

// Per-controller pick result (refreshed each frame) and ray visuals.
// `lastPick[i]` is the voxel/surface hit; `lastPanelHit[i]` is the
// hovered UI button on whichever panel is visible. Mutually exclusive
// — when the laser is on a panel, lastPick[i] is null and vice versa.
const controllerRays: ControllerRay[] = [];
const lastPick: (PickTarget | null)[] = [null, null];
const lastPanelHit: (PanelHit | null)[] = [null, null];

// 6-connected (face-adjacent) components of a grid, each a list of
// [ix, iy, iz, palette]. Two voxels touching only at an edge/corner are
// NOT connected — face-adjacency matches the intuitive "touching" rule
// for furniture blobs.
// Scratch vectors used only in the grab-bar press handler — module-
// scoped so we don't reallocate on every press.
const _grabPressOriginTmp = new THREE.Vector3();
const _grabPressDirTmp = new THREE.Vector3();
const _grabPressBarTmp = new THREE.Vector3();

// Which controller (if any) owns the current grid-mutation transaction.
// Set in selectstart, cleared in selectend. Used to serialize concurrent
// trigger presses — second controller's press is ignored until the first
// releases.
let activeTxnOwner: number | null = null;

for (let i = 0; i < 2; i++) {
  const controller = renderer.xr.getController(i);
  scene.add(controller);

  const grip = renderer.xr.getControllerGrip(i);
  grip.add(controllerModelFactory.createControllerModel(grip));
  scene.add(grip);

  // Initial tint reflects the active tool (standalone if set, else
  // action mode). Updated via syncRayTints when the tool changes.
  controllerRays.push(
    createControllerRay(controller, activeObject.group, scene, activeRayTint()),
  );

  controller.addEventListener("connected", (event: { data: XRInputSource }) => {
    controllerStates[i].inputSource = event.data;
  });
  controller.addEventListener("disconnected", () => {
    controllerStates[i].inputSource = null;
  });
  controller.addEventListener("squeezestart", () => {
    controllerStates[i].squeezing = true;
  });
  controller.addEventListener("squeezeend", () => {
    controllerStates[i].squeezing = false;
  });
  controller.addEventListener("selectstart", () => {
    controllerStates[i].selecting = true;
    // Non-dominant hand: trigger doesn't pick or click. Instead it
    // engages precision aim on the dominant controller — dampens the
    // dominant laser's rotation deltas so the user can fine-aim at
    // small / dense voxels (Wacom-style precision toggle). Release
    // returns the laser to normal sensitivity.
    if (i === nonDominantControllerIndex()) {
      setPrecisionAim(true, dominantControllerIndex());
      return;
    }
    // Panel clicks (palette / toolkit) are non-grid: never wrap them
    // in a transaction, and let them go through even while another
    // controller owns an in-flight gesture (so the user can swap
    // palettes mid-extrude).
    if (lastPanelHit[i]) {
      const hit = lastPanelHit[i]!;
      if (hit.panel === "grabBar") {
        // Trigger-and-drag the canvas itself. Slightly heavier haptic
        // than a normal panel-button tap so the "grab" reads distinctly.
        // Capture the world-space offset from the bar's center to the
        // press point so the drag preserves "grab where you pressed"
        // rather than snapping the bar's center under the cursor. Use
        // laserLocalDir + controllerEffectiveWorldPos so the captured
        // ray matches the visible laser (precision-aim override
        // included if engaged).
        controllerEffectiveWorldPos(i, _grabPressOriginTmp);
        laserLocalDir(i, _grabPressDirTmp);
        _grabPressDirTmp.transformDirection(controller.matrixWorld).normalize();
        const hitOffset = new THREE.Vector3()
          .copy(_grabPressDirTmp)
          .multiplyScalar(hit.distance)
          .add(_grabPressOriginTmp);
        grabBarMesh.getWorldPosition(_grabPressBarTmp);
        hitOffset.sub(_grabPressBarTmp);
        setInteraction({
          kind: "canvas_drag",
          controllerIndex: i,
          hitDist: hit.distance,
          hitOffset,
        });
        pulseHaptic(i, 0.5, 20);
        return;
      }
      if (hit.panel === "exitButton") {
        pulseHaptic(i, 0.5, 20);
        const session = renderer.xr.getSession();
        if (session) session.end();
        return;
      }
      handlePanelClick(hit);
      return;
    }
    // Grid-mutating gestures: only one at a time (the first presser owns
    // the transaction until release). Shared with the desktop mouse seam.
    beginGridAction(i);
  });
  controller.addEventListener("selectend", () => {
    controllerStates[i].selecting = false;
    // Non-dominant hand: release the precision-aim mode it engaged.
    // selectstart never claimed a transaction for this hand, so the
    // tx-finalization below would be a no-op anyway.
    if (i === nonDominantControllerIndex()) {
      setPrecisionAim(false, -1);
      return;
    }
    finalizeInteraction(i);
  });

  controllers.push(controller);
  grips.push(grip);
}

// Start a grid-mutating gesture for pointer `i` (VR controller or the
// desktop mouse). Only one at a time — the first presser owns the
// transaction until it releases.
function beginGridAction(i: number): void {
  if (activeTxnOwner !== null) return;
  if (interaction.kind !== "idle") return;
  activeTxnOwner = i;
  beginTransaction();
  applyToolAction(i);
}

// Finalize whatever gesture pointer `i` owns, drop to idle, commit the
// transaction. Shared by the VR selectend + the desktop mouse-up. Per-kind
// commit work lives in the owning tools/ slice (routed by the LIVE interaction
// kind); kinds with no owner just fall through to the generic tail.
function finalizeInteraction(i: number): void {
  if (activeTxnOwner !== i) return;
  tools.commitFor(interaction.kind, i);
  if (interaction.kind !== "idle") setInteraction({ kind: "idle" });
  setGestureObject(-1);
  endTransaction();
  activeTxnOwner = null;
}

// Trigger-press action: dispatch the press to the owning tools/ slice (sketch
// shapes, select, move, resize — all registered in the tool registry). Panel
// clicks are handled directly in selectstart so they bypass the transaction
// wrapper; anything that gets here is a grid action.
function applyToolAction(controllerIndex: number): void {
  if (interaction.kind !== "idle") return;
  tools.startFor(controllerIndex);
}

function handlePanelClick(hit: PanelHit): void {
  if (hit.panel === "palette") {
    setState("currentPaletteIndex", hit.swatch);
    return;
  }
  if (hit.panel === "settings") {
    switch (hit.button) {
      case "close":
        settingsPanelOpen = false;
        break;
      case "dom_left":
        setState("dominantHand", "left");
        break;
      case "dom_right":
        setState("dominantHand", "right");
        break;
      case "passthrough": {
        // AR-only: hide/show the immersive skybox to flip between
        // passthrough and an opaque background. No-op outside alpha-blend.
        const session = renderer.xr.getSession();
        if (!session || session.environmentBlendMode !== "alpha-blend") break;
        setState("passthroughActive", !uiState.passthroughActive);
        immersiveSkybox.visible = !uiState.passthroughActive;
        break;
      }
      case "recenter":
        recenterCanvas();
        break;
      case "help":
        // Swap the settings panel out for the help panel at the same spot.
        settingsPanelOpen = false;
        helpPanelOpen = true;
        summonPanelAtGaze(helpPanelMesh, -0.15);
        break;
    }
    return;
  }
  if (hit.panel === "help") {
    if (hit.button === "close") helpPanelOpen = false;
    return;
  }
  if (hit.panel !== "toolkit") return;
  switch (hit.button) {
    // Primary tools — explicit, always switch.
    case "draw":
      setTopMode("sketch");
      break;
    case "paint":
      setTopMode("paint");
      break;
    case "bucket":
      setTopMode("bucket");
      break;
    case "select":
      setTopMode("select");
      break;
    case "move":
      setTopMode("move");
      break;
    case "resize":
      setTopMode("resize");
      break;
    case "grid":
      // The meshers track the flag via an onState subscription, so a new
      // scene object can't be missed by this toggle.
      setState("gridLinesVisible", !uiState.gridLinesVisible);
      break;
    case "edges":
      setState("edgesVisible", !uiState.edgesVisible);
      break;
    case "fill":
      fillCanvas();
      triggerFillShield();
      break;
    case "clear":
      clearCanvas();
      break;
    case "settings":
      // Open settings exactly where the toolkit is sitting, and dismiss
      // the toolkit (free its summon slot). Because the slot is claimed on
      // a rising edge, the toolkit won't pop back while the A button is
      // still held — the user releases and re-holds A to bring it back.
      // Settings is sticky: it stays put (even if the toolkit is later
      // re-summoned) until its own X is tapped. Guarded so a second tap
      // can't re-anchor / "reopen" an already-open settings window.
      if (settingsPanelOpen) break;
      settingsPanelOpen = true;
      settingsPanelMesh.position.copy(toolkitPanelMesh.position);
      settingsPanelMesh.quaternion.copy(toolkitPanelMesh.quaternion);
      clearPanelSummon();
      break;
  }
}

// Settings + help panels are summoned once from their toolkit buttons
// and persist until the user taps the X close button. Unlike the
// toolkit / palette panels (which only show while their face button is
// held), these are sticky panels — clicking inside them doesn't
// dismiss them, only their close button does.
let settingsPanelOpen = false;
let helpPanelOpen = false;

// Delete every currently-selected cell from the grid in a single
// transaction so the action is one undo step. No-op when the
// selection is empty. Clears the selection on commit since the cells
// it referred to no longer exist.
// Undo/redo the voxel grid. The selection is restored by the history itself
// (each transaction records the selection before/after), so undoing a move keeps
// the cells selected, and undoing the select step before it lifts the selection.
// Undo/redo walk the single model timeline — voxel edits, selection steps,
// and reference-image transforms interleaved in the order they happened.
function modelUndo(): boolean {
  return undo(grid);
}
function modelRedo(): boolean {
  return redo(grid);
}

// (deleteSelection lives in core/edit-core/selection now.)

// Invert the selection: select every filled voxel that ISN'T currently selected
// (and drop those that are). With nothing selected this acts as "select all".
// Recorded as its own undoable selection-only step, like the marquee / deselect.
function invertSelection(): void {
  const before = selectionStateString();
  const inverted: { ix: number; iy: number; iz: number }[] = [];
  for (const [ix, iy, iz] of grid.entries()) {
    if (!selection.has(ix, iy, iz)) inverted.push({ ix, iy, iz });
  }
  clearSelFloat(); // replacing the selection commits any pending duplicate
  selection.replace(inverted);
  commitSelectionChange(before, selectionStateString());
}

// ── Voxel clipboard ─────────────────────────────────────────────────────────
// Copy / paste / duplicate (+ the pending-placement mechanic and adoptSelFloat)
// live in the core/edit-core/clipboard slice, installed below the AppContext —
// it registers its own Cmd/Ctrl C/V/D shortcuts. The gesture code calls
// clipboard.adoptSelFloat at move-grab time.

// Fill the entire work volume with the current palette colour — one undo step.
// (fillCanvas / clearCanvas / rotateSelection / flipSelection live in
// core/edit-core/canvas-ops. The canvas TRANSFORM reset below stays — it's a
// view concern, not a grid edit.)

// Reset world + gridGroup transforms to their session defaults so the
// user can recover from a lost canvas (drifted behind them, scaled to
// nothing, rotated off-axis, etc). Mirrors the VR/desktop init paths.
function recenterCanvas(): void {
  if (renderer.xr.isPresenting) {
    gridGroup.position.copy(VR_GRID_POSITION);
    gridGroup.rotation.set(VR_GRID_ROTATION_X, 0, 0);
    gridGroup.scale.setScalar(vrGridScale());
  } else {
    gridGroup.position.copy(DESKTOP_GRID_POSITION);
    gridGroup.rotation.set(DESKTOP_GRID_ROTATION_X, 0, 0);
    gridGroup.scale.setScalar(1);
  }
  world.position.set(0, 0, 0);
  world.rotation.set(0, 0, 0);
  world.scale.set(1, 1, 1);
  // Force the grab bar to re-pose on the next frame instead of
  // sticking with its old user-facing yaw (which was computed against
  // the pre-recenter canvas position).
  resetGrabBarPosition();
  // Desktop: reframe the camera on the reset volume so dragging still
  // pivots around it and the view is recovered.
  if (!renderer.xr.isPresenting) frameWorkVolume();
}

function setTopMode(mode: TopMode): void {
  setState("topMode", mode); // ray tints follow via the onState subscription
}

function activeRayTint(): "draw" | "erase" | "marquee" | "move" {
  // SELECT tool: laser is always violet (the marquee/selection color),
  // whether you're hovering or actively dragging a box — regardless of
  // the move/delete select-action.
  if (uiState.topMode === "select") return "marquee";
  if (uiState.topMode === "move") return "move";
  // PAINT and BUCKET apply the active palette colour to existing voxels — tint
  // the laser like DRAW (not ERASE/red), regardless of the last draw/erase action.
  if (uiState.topMode === "paint" || uiState.topMode === "bucket") return "draw";
  return uiState.actionMode;
}

function syncRayTints(): void {
  const tint = activeRayTint();
  for (const ray of controllerRays) setControllerRayTint(ray, tint);
}

// ── Derived-state subscriptions ────────────────────────────────────────────
// Derived state that must track a uiState field, registered ONCE here instead
// of being hand-called from every write site (all writes go through setState).
onState("topMode", syncRayTints);
onState("actionMode", syncRayTints);
onState("selectAction", syncRayTints);
// Every mesher mirrors the visibility toggles — a scene object created later
// still applies the current flags at construction, but can no longer be
// missed by the toggle itself.
onState("gridLinesVisible", () => {
  setAllGridVisible(uiState.gridLinesVisible);
  raymarchView?.setShowGrid(uiState.gridLinesVisible);
  boxPreview.setLineVisible(uiState.gridLinesVisible, uiState.edgesVisible);
});
onState("edgesVisible", () => {
  setAllEdgesVisible(uiState.edgesVisible);
  raymarchView?.setShowEdges(uiState.edgesVisible);
  boxPreview.setLineVisible(uiState.gridLinesVisible, uiState.edgesVisible);
});
// Preferences that survive a reload (hydrated just before the boot setMode).
onState("dominantHand", () => saveSetting("dominantHand", uiState.dominantHand));
onState("gridLinesVisible", () =>
  saveSetting("gridLinesVisible", uiState.gridLinesVisible),
);
onState("edgesVisible", () => saveSetting("edgesVisible", uiState.edgesVisible));
// presentationLighting is intentionally NOT persisted: the Lighting rig is a
// preview/presentation mode that should start OFF every session, not remember a
// past toggle. (Default lives in uiState; off in both 3D desktop + walkthrough.)

// Scratch for the shared pick path (the VR/desktop input layers keep their own
// private copies; every use writes before it reads, so the copies never
// interact).
const _pickOriginTmp = new THREE.Vector3();
const _grabBarWorldPosTmp = new THREE.Vector3();
// VR pick loop + the desktop pick. Returns a shared, per-call-overwritten
// object (this runs per pointer per frame) — callers consume it immediately.
const _masksTmp: {
  skip: ReadonlySet<string> | undefined;
  include: ReadonlySet<string> | undefined;
  includeBox: CellBox | null;
  skipBox: CellBox | null;
} = { skip: undefined, include: undefined, includeBox: null, skipBox: null };
function setMasks(
  skip: ReadonlySet<string> | undefined,
  include: ReadonlySet<string> | undefined,
  includeBox: CellBox | null,
  skipBox: CellBox | null = null,
): typeof _masksTmp {
  _masksTmp.skip = skip;
  _masksTmp.include = include;
  _masksTmp.includeBox = includeBox;
  _masksTmp.skipBox = skipBox;
  return _masksTmp;
}
function gestureRaycastMasks(i: number): typeof _masksTmp {
  if (interaction.kind === "voxel_paint" && interaction.controllerIndex === i) {
    return setMasks(interaction.placedCells, undefined, null);
  }
  if (interaction.kind === "voxel_erase" && interaction.controllerIndex === i) {
    // Keep this gesture's erased cells collidable so the cursor stays on the
    // original surface depth instead of drilling through the hole it just made.
    return setMasks(undefined, interaction.placedCells, null);
  }
  if (interaction.kind === "box_draw" && interaction.controllerIndex === i) {
    // A LIVE box_draw fills in bulk (no per-cell placedKeys), so the box it has
    // drawn so far (appliedBox) stands in as a skipBox — the ray looks PAST it to
    // the original surface instead of the box deflecting its own target cell.
    return setMasks(interaction.placedKeys, undefined, null, interaction.appliedBox ?? null);
  }
  if (interaction.kind === "circle_draw" && interaction.controllerIndex === i) {
    return setMasks(interaction.placedKeys, undefined, null);
  }
  if (interaction.kind === "box_erase" && interaction.controllerIndex === i) {
    // The live carve writes no per-cell set, so the region already carved (the
    // box reflected in appliedBox) stands in as the include — the laser stops at
    // the original surface instead of drilling deeper as it erodes.
    return setMasks(undefined, undefined, interaction.appliedBox ?? null);
  }
  if (interaction.kind === "circle_erase" && interaction.controllerIndex === i) {
    return setMasks(undefined, interaction.placedKeys, null);
  }
  if (interaction.kind === "extrude_erase" && interaction.controllerIndex === i) {
    // Bulk (rectangular) erase uses the carved slab as the include region so the
    // cursor stays on the surface; the mask path keeps its per-cell ghost set.
    const box = extrudeEraseIncludeBox();
    if (box) return setMasks(undefined, undefined, box);
    return setMasks(undefined, interaction.editedKeys, null);
  }
  return setMasks(undefined, undefined, null);
}

// Multi-object pick for pointer `i`. During an active draw gesture, restrict
// to the object being drawn into (with its skip/include); otherwise hover-
// pick the nearest voxel across all objects plus a room-surface hit. Sets
// lastPickObject[i] and returns the hit + the object frame the cursor should
// render in. Shared by the VR pick loop + the desktop pick.
// Reused pickRay input + multiObjectPick result — this path runs once per
// object per pointer per frame, so no per-call literals. Every field is
// (re)assigned before each pickRay call; callers destructure the result
// immediately, so the shared instance can't alias across frames.
const _pickInTmp: PickInput = {
  originWorld: null as unknown as THREE.Vector3,
  dirWorld: null as unknown as THREE.Vector3,
  grid: null as unknown as VoxelGrid,
  gridGroup: null as unknown as THREE.Object3D,
  voxelSpace: undefined,
  includeSurfaces: undefined,
  workVolumeSurfaces: [],
  bounds: null as unknown as CellBounds,
  voxelRaycastSkip: undefined,
  voxelRaycastInclude: undefined,
  voxelRaycastIncludeBox: null,
  voxelRaycastSkipBox: null,
};
function runPickRay(
  originWorld: THREE.Vector3,
  dirWorld: THREE.Vector3,
  grid: VoxelGrid,
  voxelSpace: THREE.Object3D,
  includeSurfaces: boolean,
  skip: ReadonlySet<string> | undefined,
  include: ReadonlySet<string> | undefined,
  includeBox: CellBox | null,
  skipBox: CellBox | null,
): PickTarget {
  _pickInTmp.originWorld = originWorld;
  _pickInTmp.dirWorld = dirWorld;
  _pickInTmp.grid = grid;
  _pickInTmp.gridGroup = gridGroup;
  _pickInTmp.voxelSpace = voxelSpace;
  _pickInTmp.includeSurfaces = includeSurfaces;
  _pickInTmp.workVolumeSurfaces = workVolumeSurfaces;
  _pickInTmp.bounds = cellBounds;
  _pickInTmp.voxelRaycastSkip = skip;
  _pickInTmp.voxelRaycastInclude = include;
  _pickInTmp.voxelRaycastIncludeBox = includeBox;
  _pickInTmp.voxelRaycastSkipBox = skipBox;
  return pickRay(_pickInTmp);
}
const _multiPickResult: { pick: PickTarget; group: THREE.Object3D } = {
  pick: null,
  group: null as unknown as THREE.Object3D,
};
function multiObjectPick(
  i: number,
  originWorld: THREE.Vector3,
  dirWorld: THREE.Vector3,
  skip: ReadonlySet<string> | undefined,
  include: ReadonlySet<string> | undefined,
  includeBox: CellBox | null,
  skipBox: CellBox | null = null,
): { pick: PickTarget; group: THREE.Object3D } {
  let pick: PickTarget = null;
  let group: THREE.Object3D = gridGroup;
  const drawingHere =
    gestureActive() &&
    "controllerIndex" in interaction &&
    interaction.controllerIndex === i;
  if (drawingHere) {
    const g = gestureObject();
    pick = runPickRay(
      originWorld,
      dirWorld,
      g.grid,
      g.group,
      isRoomAligned(g.group),
      skip,
      include,
      includeBox,
      skipBox,
    );
    lastPickObject[i] = g.id;
    group = g.group;
  } else {
    for (const o of sceneObjects) {
      const p = runPickRay(
        originWorld,
        dirWorld,
        o.grid,
        o.group,
        false,
        undefined,
        undefined,
        null,
        null,
      );
      if (p?.kind === "voxel" && (!pick || p.distance < pick.distance)) {
        pick = p;
        lastPickObject[i] = o.id;
        group = o.group;
      }
    }
    const room = findRoomObject();
    const surf = runPickRay(
      originWorld,
      dirWorld,
      room ? room.grid : surfaceFallbackGrid,
      gridGroup,
      true,
      undefined,
      undefined,
      null,
      null,
    );
    if (surf?.kind === "surface" && (!pick || surf.distance < pick.distance)) {
      pick = surf;
      lastPickObject[i] = ROOM_TARGET;
      group = room ? room.group : gridGroup;
    }
  }
  _multiPickResult.pick = pick;
  _multiPickResult.group = group;
  return _multiPickResult;
}

// Per-frame: raycast from each connected controller, stash the result
// in lastPick[i] / lastPanelHit[i], and update the ray + cursor visuals.
// UI panels take priority over voxel/surface picks when visible, so the


// Dominant hand owns: panel-summon face buttons + shape / select-action
// dial. Non-dominant hand owns: undo/redo face buttons + action dial.
// Switching uiState.dominantHand from the SETTINGS panel flips all
// these bindings to the opposite controller.
function dominantHandedness(): Handedness {
  return uiState.dominantHand;
}
function nonDominantHandedness(): Handedness {
  return uiState.dominantHand === "right" ? "left" : "right";
}
function dominantControllerIndex(): number {
  // Desktop has a single synthetic pointer (controller 0); no handedness.
  if (!renderer.xr.isPresenting) return 0;
  return findControllerIndexByHandedness(dominantHandedness());
}
function nonDominantControllerIndex(): number {
  return findControllerIndexByHandedness(nonDominantHandedness());
}

// Both controllers' face buttons + analog squeeze are polled here

function setActionMode(mode: ActionMode): void {
  setState("actionMode", mode); // ray tints follow via the onState subscription
}

function setShapeMode(mode: ShapeMode): void {
  setState("shapeMode", mode);
}

// Pick a sketch shape from a keyboard shortcut: set the shape and, since the
// Shape buttons only show while drawing, make sure a sketch tool is active so the
// choice is visible/usable (the current draw/erase action is kept).
function selectShapeHotkey(mode: ShapeMode): void {
  setShapeMode(mode);
  if (uiState.topMode !== "sketch") setTopMode("sketch");
}

// Eyedropper press handler (wired into the desktop input seam). When the color
// editor has armed the dropper, an LMB press samples the voxel under the cursor:
// read its stored palette index from that object's grid and hand the EXACT
// "#rrggbb" back to the editor's callback. Returns true while armed so the press
// is swallowed (no draw/erase/select); a click that misses a voxel stays armed
// for another try. Returns false when nothing is armed (normal tool press).
function tryEyedropSample(): boolean {
  if (!eyedropperArmed()) return false;
  const pick = lastPick[0];
  if (pick?.kind === "voxel") {
    const idx = objectById(lastPickObject[0]).grid.get(pick.ix, pick.iy, pick.iz);
    if (idx > 0) deliverEyedrop(paletteColorHex(idx));
  }
  return true;
}

// Toggle the 2D / 3D view for whichever mode is active: model = perspective ↔
// orthographic, floor = 3D ↔ top-down plan. Bound to the rebindable "view-toggle"
// hotkey, which works in both modes.
function toggle2D3D(): void {
  if (uiState.mode === "floorplan") {
    setFloorView(uiState.floorView === "2d" ? "3d" : "2d");
  } else {
    setModelOrthographic(!modelOrthographic);
  }
}

// Behaviour for each rebindable hotkey id. The keys themselves live in
// hotkeys.ts (shared by the keymap + Settings overlay); this maps an action id to
// what it does. Ids match HOTKEY_DEFS in hotkeys.ts.
const HOTKEY_ACTIONS: Record<string, () => void> = {
  "tool-create": () => {
    setTopMode("sketch");
    setActionMode("draw");
  },
  "tool-erase": () => {
    setTopMode("sketch");
    setActionMode("erase");
  },
  // Select is the same tool in both modes, so its key works in either.
  "tool-select": () =>
    uiState.mode === "floorplan" ? setFloorTool("select") : setTopMode("select"),
  "tool-move": () => setTopMode("move"),
  "tool-paint": () => setTopMode("paint"),
  "tool-bucket": () => setTopMode("bucket"),
  "shape-box": () => selectShapeHotkey("box"),
  "shape-extrude": () => selectShapeHotkey("extrude"),
  "shape-voxel": () => selectShapeHotkey("voxel"),
  "shape-circle": () => selectShapeHotkey("circle"),
  "shape-freeform": () => selectShapeHotkey("freeform"),
  "select-invert": () => invertSelection(),
  // Layout-mode tools (dispatched only in floor mode — see the keymap).
  "layout-wall": () => setFloorTool("wall"),
  "layout-measure": () => setFloorTool("measure"),
};
// Hotkey actions reachable while in layout (floor) mode.
const FLOOR_HOTKEY_ACTIONS = new Set(["tool-select", "layout-wall", "layout-measure"]);

// Open the desktop Settings overlay. The Tools/Shape rows are rebindable (owned
// by hotkeys.ts); here we just supply the fixed reference shortcuts and a
// re-render hook so the sidebar's key hints refresh after a rebind.
function openSettings(): void {
  const mod = /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
  const fixed: ShortcutSection[] = [
    {
      group: "Edit",
      items: [
        { keys: `${mod} Z`, label: "Undo" },
        { keys: `${mod} ⇧ Z  /  ${mod} Y`, label: "Redo" },
        { keys: `${mod} C`, label: "Copy selection" },
        { keys: `${mod} V`, label: "Paste" },
        { keys: `${mod} D`, label: "Duplicate selection" },
        { keys: "Delete", label: "Delete selection" },
        { keys: "Esc", label: "Deselect" },
      ],
    },
    {
      group: "Other",
      items: [
        { keys: `${mod} S`, label: "Save" },
        { keys: `${mod} ⇧ S`, label: "Save As" },
      ],
    },
  ];
  showSettings({
    fixed,
    onChange: () => {
      // Refresh both sidebars' key hints after a rebind.
      desktopUIRef?.render();
      floorUI.render();
    },
  });
}

function setSelectAction(mode: SelectAction): void {
  setState("selectAction", mode); // ray tints follow via the onState subscription
}


// ── Desktop Controls ─────────────────────────────────────────────────────

// Custom trackball controller for the perspective camera (modeling + floor-3D).
// Unlike OrbitControls it has NO fixed up and no polar clamp, so you can keep
// tumbling OVER the poles like Blender — the view can roll fully upside-down.
// RMB rotates, MMB pans, wheel zooms; LMB is left for the tool. (The 2D floor
// ortho camera below keeps real OrbitControls — it never rotates.)
const orbitControls = new Orbit3D(camera, renderer.domElement);
// Scale panning by the distance to whatever's under the cursor — but only in the
// model 3D (perspective) view, where zooming dollies the eye toward the pivot and
// collapses the pivot distance. In 2D ortho and the floor view the pivot distance
// is the right reference, so return null (use the default).
orbitControls.panDistance = () =>
  uiState.mode === "modeling" && !modelOrthographic
    ? (lastPick[0]?.distance ?? null)
    : null;
// Orbit pivot: the world point under the cursor at right-drag start, so the view
// rotates around what you're looking at instead of the stale WASD-flown pivot.
// Model 3D uses the precise voxel/surface hit; floor 3D falls back to the cursor
// point on the floor plane (y = 0). Null over empty space → keep the old pivot.
const _pivotPt = new THREE.Vector3();
const _pivotDir = new THREE.Vector3();
const cursorRayDir = (): THREE.Vector3 =>
  _pivotDir
    .set(desktopMouse.ndc.x, desktopMouse.ndc.y, 0.5)
    .unproject(camera)
    .sub(camera.position)
    .normalize();
orbitControls.pivotPick = () => {
  if (uiState.mode === "modeling" && !modelOrthographic) {
    const d = lastPick[0]?.distance;
    if (d == null) return null;
    return _pivotPt.copy(camera.position).addScaledVector(cursorRayDir(), d);
  }
  if (uiState.mode === "floorplan" && uiState.floorView === "3d") {
    const dir = cursorRayDir();
    if (Math.abs(dir.y) < 1e-4) return null; // ray parallel to the floor
    const t = -camera.position.y / dir.y;
    if (t <= 0) return null; // floor is behind the camera
    return _pivotPt.copy(camera.position).addScaledVector(dir, t);
  }
  return null;
};
installDesktopMouse(renderer.domElement);
const desktopMarqueeRect = installDesktopMarqueeRect();

// Frame the desktop camera on the current work volume: aim the orbit target
// at the volume center and pull back from a 3/4 angle far enough to fit it.
// Also (re)derives zoom limits from the volume size. Bound to the F / Home
// keys and used by recenter / VR-exit so the user can always recover a view.
const _frameCenter = new THREE.Vector3();
const _frameDir = new THREE.Vector3(0.55, 0.6, 0.95).normalize();
function frameWorkVolume(): void {
  gridGroup.updateMatrixWorld(true);
  const b = volumeBounds;
  _frameCenter
    .set((b.minX + b.maxX) / 2, b.height / 2, (b.minZ + b.maxZ) / 2)
    .applyMatrix4(gridGroup.matrixWorld);
  const radius =
    0.5 *
    Math.hypot(b.maxX - b.minX, b.height, b.maxZ - b.minZ) *
    gridGroup.scale.x;
  const fov = THREE.MathUtils.degToRad(camera.fov);
  const dist = (radius / Math.sin(fov / 2)) * 1.3;
  orbitControls.target.copy(_frameCenter);
  camera.position.copy(_frameCenter).addScaledVector(_frameDir, dist);
  camera.up.set(0, 1, 0); // recover upright after any over-pole tumbling
  camera.lookAt(_frameCenter);
  orbitControls.minDistance = radius * 0.15;
  orbitControls.maxDistance = radius * 12;
  orbitControls.update();
}
frameWorkVolume();

// Optional orthographic projection for Model mode (parallel, no foreshortening —
// makes precise edge/section selection easier). It's a slaved camera: Orbit3D
// still drives the perspective `camera` as the source of truth, and each frame
// this one is synced to match its position/orientation, with a frustum sized so
// the on-screen scale (and the orbit zoom) matches what perspective showed.
let modelOrthographic = false;
const modelOrthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
function syncModelOrthoCamera(): void {
  camera.updateMatrixWorld();
  modelOrthoCamera.position.copy(camera.position);
  modelOrthoCamera.quaternion.copy(camera.quaternion);
  const dist = Math.max(1e-3, camera.position.distanceTo(orbitControls.target));
  const halfH = dist * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  const halfW = halfH * camera.aspect;
  modelOrthoCamera.left = -halfW;
  modelOrthoCamera.right = halfW;
  modelOrthoCamera.top = halfH;
  modelOrthoCamera.bottom = -halfH;
  // Symmetric depth slab centred on the eye so nothing clips at any zoom (ortho
  // depth is linear, so the wide range costs no precision).
  modelOrthoCamera.near = -camera.far;
  modelOrthoCamera.far = camera.far;
  // Match the perspective camera's sidebar shift so toggling doesn't jump.
  modelOrthoCamera.setViewOffset(
    window.innerWidth,
    window.innerHeight,
    -DESKTOP_SIDEBAR_W / 2,
    0,
    window.innerWidth,
    window.innerHeight,
  );
  modelOrthoCamera.updateMatrixWorld();
}


// Per-item layout visibility, as a registry (ui/visibility-registry). Each "Show
// X" toggle is lit when its item is VISIBLE. Walls + models show by default;
// dimensions + measurements are opt-in clutter; a fresh layout also turns wall
// dimensions on (newLayoutOn). Adding a toggle = one register() call here — the
// toolbar builds its button and applyAll()/reset honour it automatically.
const floorVisibility = createVisibilityRegistry();
floorVisibility.register({
  id: "walls",
  label: "Walls",
  defaultOn: true,
  newLayoutOn: true,
  apply: (on) => wallSystem.setVisible(on),
  onToggle: (on) => {
    if (!on) selectFloorVertex(null); // hidden walls show no corner padlock
  },
});
floorVisibility.register({
  id: "models",
  label: "Models",
  defaultOn: true,
  newLayoutOn: true,
  apply: (on) => placementSystem.setVisible(on),
  // Turning the master switch off then back on reveals every per-model-hidden
  // model (right-click "Hide model") — an easy escape hatch. Commit only when a
  // hidden model actually existed (undo step).
  onToggle: (on) => {
    if (on && placementSystem.showAll()) commitFloor();
  },
});
floorVisibility.register({
  id: "measurements",
  label: "Measurements",
  defaultOn: false, // off by default; the Measure tool still auto-shows them while active
  apply: (on) => setMeasurementsPinned(on),
});
floorVisibility.register({
  id: "wall-dims",
  label: "Wall dimensions",
  defaultOn: false,
  newLayoutOn: true,
  apply: (on) => wallLabels.setVisible(on),
});
floorVisibility.register({
  id: "model-dims",
  // Bulk default for every model that hasn't been individually overridden via
  // the right-click "Show/Hide dimensions" menu. The floor render loop reads
  // this each frame (per-model override ?? this toggle), so apply just wakes a
  // frame rather than force-toggling the whole group (which would stomp the
  // per-model overrides for a frame).
  label: "Model dimensions",
  defaultOn: false,
  apply: () => wakeRendering(),
  // Master switch: operating it clears every per-model override so all models
  // follow it — an individually-shown model turns off when you toggle all off,
  // and vice-versa. Commit only when an override actually existed (undo step).
  onToggle: () => {
    if (placementSystem.clearDimsOverrides()) commitFloor();
  },
});
floorVisibility.register({
  id: "opening-dims",
  label: "Door/window dimensions",
  defaultOn: false,
  apply: (on) => setAllOpeningDimsVisible(on),
});
// Reset the toggles for a brand-new (blank) layout (walls + models + wall
// dimensions on); a loaded layout leaves the current toggles untouched.
function resetLayoutVisibility(): void {
  floorVisibility.resetForNewLayout();
  floorUI.render();
}

// Slice-contributed floor-sidebar sections. The visibility "Show" group is the
// first: built from the visibility registry, so a new toggle is one register()
// call above — the section picks it up with no floor-ui edits.
const floorSections = createSectionRegistry();
floorSections.register({
  id: "floor-visibility",
  build: (host) => {
    const grp = host.group("Show");
    grp.classList.add("vis-list");
    // Collapsible: click the "Show" header to fold the toggle list away (a caret
    // rotates). The fold state persists across reloads (settings-store).
    const header = grp.querySelector<HTMLElement>(".lbl");
    if (header) {
      header.classList.add("clickable");
      header.setAttribute("role", "button");
      header.tabIndex = 0;
      const applyCollapsed = (collapsed: boolean) => {
        grp.classList.toggle("collapsed", collapsed);
        header.setAttribute("aria-expanded", String(!collapsed));
      };
      applyCollapsed(loadSettings().floorShowCollapsed ?? false);
      const toggleCollapsed = () => {
        const next = !grp.classList.contains("collapsed");
        applyCollapsed(next);
        saveSetting("floorShowCollapsed", next);
      };
      header.addEventListener("click", toggleCollapsed);
      header.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          toggleCollapsed();
        }
      });
    }
    // Each item is a full-width row: name on the left, a toggle switch on the
    // right (lit when the item is visible). See .vis-row CSS in floor-ui.ts.
    const rows = new Map<string, HTMLButtonElement>();
    for (const item of floorVisibility.list()) {
      const row = document.createElement("button");
      row.className = "vis-row";
      const label = document.createElement("span");
      label.className = "vis-label";
      label.textContent = item.label;
      const sw = document.createElement("span");
      sw.className = "vis-switch";
      const knob = document.createElement("span");
      knob.className = "vis-knob";
      sw.appendChild(knob);
      row.append(label, sw);
      row.addEventListener("click", () => {
        floorVisibility.toggle(item.id);
        floorUI.render();
      });
      grp.appendChild(row);
      rows.set(item.id, row);
    }
    return {
      render: () => {
        // Each row's switch is lit while its item is visible.
        for (const item of floorVisibility.list()) {
          const row = rows.get(item.id);
          if (!row) continue;
          row.classList.toggle("vis-on", item.on);
          row.setAttribute("aria-pressed", String(item.on));
        }
      },
    };
  },
});

// Imperative app lifecycle events. Floor-mode emits "new-layout" when it blanks
// the layout; reactions register here instead of as FloorModeCtx callbacks.
const lifecycle = createLifecycle();
lifecycle.on("new-layout", () => resetLayoutVisibility());

// Top-down orthographic camera + its own pan/zoom controls for the 2D plan
// view. The 3D floor view reuses the perspective camera + orbitControls.
let orthoHalfHeight = 8; // metres visible vertically; OrbitControls zoom scales it
const orthoCamera = new THREE.OrthographicCamera(-10, 10, 10, -10, 0.01, 500);
orthoCamera.up.set(0, 0, -1); // north-up plan: +Z reads as "down" on screen
orthoCamera.position.set(0, 100, 0);
orthoCamera.lookAt(0, 0, 0);
function sizeOrthoCamera(): void {
  const w = window.innerWidth;
  const h = window.innerHeight;
  const aspect = w / h;
  orthoCamera.left = -orthoHalfHeight * aspect;
  orthoCamera.right = orthoHalfHeight * aspect;
  orthoCamera.top = orthoHalfHeight;
  orthoCamera.bottom = -orthoHalfHeight;
  // Shift right by half the sidebar, like the perspective camera, so the plan
  // centers in the workable area. setViewOffset recomputes the projection.
  orthoCamera.setViewOffset(w, h, -DESKTOP_SIDEBAR_W / 2, 0, w, h);
}
sizeOrthoCamera();

const floor2DControls = new OrbitControls(orthoCamera, renderer.domElement);
floor2DControls.enableRotate = false; // plan view never tumbles (stays top-down)
floor2DControls.screenSpacePanning = true;
floor2DControls.mouseButtons = {
  LEFT: null as unknown as THREE.MOUSE, // free for floor tools
  MIDDLE: THREE.MOUSE.PAN,
  RIGHT: null as unknown as THREE.MOUSE, // right-drag orbits the view (below)
};
floor2DControls.enabled = false;
// True while the plan view is mid-snap to an axis-aligned orthographic view
// (driven by the corner nav gizmo or a right-drag release; see
// startPlanViewAlign / updatePlanViewTween / installAxisSnapOrbit).
let _planAlignActive = false;
// How far from the target the plan camera sits — within the ortho near/far.
const PLAN_VIEW_DIST = 100;

// Axis-snap orbit (right-drag) lives in app/axis-snap-orbit; axis math in
// view/axis-math. Wired to each camera/controls set below.
// 2D floor plan — axis-snap orbit over the plan ortho camera (omits −Y).
installAxisSnapOrbit(renderer.domElement, {
  isActive: () => uiState.mode === "floorplan" && uiState.floorView === "2d",
  axes: PLAN_AXES,
  target: () => floor2DControls.target,
  eyePos: () => orthoCamera.position,
  camUp: () => orthoCamera.up,
  snap: (axis, up) => startPlanViewAlign(axis, up),
  cancelTween: () => {
    _planAlignActive = false;
  },
  // The bird's-eye (±Y) view carries the swipe's roll (rotating around and back
  // can leave a different axis "down"); side elevations always reset to +Y-up,
  // so the floor's vertical never tips sideways or upside-down.
  resolveUp: (axis, currentUp, out) => {
    if (Math.abs(axis.y) <= 0.5) return false; // side elevation → canonical +Y-up
    snapUpForAxis(axis, currentUp, out); // top view → keep the rolled orientation
    return true;
  },
});
// Model orthographic view — axis-snap orbit over the perspective camera (all six
// axes); the model ortho camera mirrors it each frame.
installAxisSnapOrbit(renderer.domElement, {
  isActive: () => uiState.mode === "modeling" && modelOrthographic,
  axes: MODEL_AXES,
  target: () => orbitControls.target,
  eyePos: () => camera.position,
  camUp: () => camera.up,
  snap: (axis, up) => startViewAlign(axis, up),
  cancelTween: () => {
    cancelViewAlign();
  },
  // Every axis carries the roll over — full continuous rotation (any tumble).
  resolveUp: (axis, currentUp, out) => {
    snapUpForAxis(axis, currentUp, out);
    return true;
  },
});

function activeCamera(): THREE.Camera {
  if (uiState.mode === "floorplan" && uiState.floorView === "2d") {
    return orthoCamera;
  }
  if (uiState.mode === "modeling" && modelOrthographic) return modelOrthoCamera;
  return camera;
}

// Frame the floor: 3D = perspective 3/4 from a height; 2D = ortho centered.
// Frame the floor view, centered on the layout's bounding-box center (so an
// off-origin layout is centered on screen in BOTH the 2D plan and the 3D orbit).
// Pass an explicit center to override; empty layouts fall back to the origin.
// Framing also FITS when there is a layout to fit against: an 18-ft room and a
// whole floor of them should both fill the viewport, rather than every layout
// getting the same fixed zoom. With no layout (a blank document) there is
// nothing to measure, so the old fixed framing stands.
const _frameC = new THREE.Vector3();
const _frameSize = new THREE.Vector3();
const _frameSphere = new THREE.Sphere();
// Direction the 3/4 view sits along, and the fallback distance.
// The elevation is deliberately above 45°, so +Y is the DOMINANT component:
// enter2DFromOrbit snaps to whichever world axis the 3D camera is closest to
// facing from, so a flatter 3/4 (the old (7, 7, 10) — 30°, where +Z dominates)
// made the 2D button land on a wall ELEVATION instead of the floor plan.
const FRAME_3D_DIR = new THREE.Vector3(7, 13, 10).normalize();
const FRAME_3D_DIST = Math.hypot(7, 13, 10);
// Breathing room around fitted content so walls never touch the viewport edge.
const FRAME_PAD = 1.25;
function frameFloorView(center?: THREE.Vector3): void {
  // An explicit center means "look here", not "fit this" — no bounds to fit.
  const box = center ? null : layoutBounds();
  if (center) _frameC.copy(center);
  else if (box) box.getCenter(_frameC);
  else _frameC.set(0, 0, 0);
  const cx = _frameC.x;
  const cz = _frameC.z;
  if (uiState.floorView === "3d") {
    orbitControls.target.set(cx, 0, cz);
    // Pull back along the 3/4 direction until the content's bounding sphere
    // fits the vertical FOV (the tighter axis — the viewport is wider than tall
    // even after the sidebar's view offset).
    let dist = FRAME_3D_DIST;
    if (box) {
      box.getBoundingSphere(_frameSphere);
      const halfFov = THREE.MathUtils.degToRad(camera.fov / 2);
      dist = THREE.MathUtils.clamp(
        (_frameSphere.radius / Math.sin(halfFov)) * FRAME_PAD,
        3,
        110, // stay inside orbitControls.maxDistance
      );
    }
    camera.position.copy(orbitControls.target).addScaledVector(FRAME_3D_DIR, dist);
    camera.up.set(0, 1, 0); // recover upright after any over-pole tumbling
    camera.lookAt(orbitControls.target);
    orbitControls.minDistance = 0.5;
    orbitControls.maxDistance = 120;
    orbitControls.update();
  } else {
    // Reset to the default top-down plan (a prior axis snap may have moved the
    // camera to a side and changed its up; restore the north-up orientation).
    _planAlignActive = false;
    floor2DControls.target.set(cx, 0, cz);
    orthoCamera.position.set(cx, PLAN_VIEW_DIST, cz);
    orthoCamera.up.set(0, 0, -1); // +Z reads as "down" on screen
    orthoCamera.zoom = 1;
    if (box) {
      // The plan is north-up, so world X spans the screen width and Z its
      // height. Fit whichever axis is tighter, measuring width against the area
      // left of the sidebar (what sizeOrthoCamera's view offset centers on).
      box.getSize(_frameSize);
      const usableW = Math.max(window.innerWidth - DESKTOP_SIDEBAR_W, 1);
      const aspect = usableW / window.innerHeight;
      orthoHalfHeight = Math.max(
        (_frameSize.z / 2) * FRAME_PAD,
        (_frameSize.x / 2 / aspect) * FRAME_PAD,
        1.5, // a lone wall shouldn't zoom to microscope range
      );
    } else orthoHalfHeight = 8;
    sizeOrthoCamera();
    orthoCamera.lookAt(floor2DControls.target);
    floor2DControls.update();
  }
}

// Switching plan → 3D: look down the exact same axis the plan was viewing, with
// NO added tilt — a top (bird's-eye) plan becomes a straight-down 3D view, a side
// elevation becomes a straight-on side view, just in perspective now. Pan
// (target), orientation (up), and zoom (distance) all carry over. The orbit's
// elevation clamp only kicks in once you start rotating, so this exact view is
// what renders until then.
const _enterDir = new THREE.Vector3();
function enter3DFromPlan(): void {
  orbitControls.target.copy(floor2DControls.target);
  // Direction from the target to the plan camera = the axis the plan looks down.
  _enterDir.copy(orthoCamera.position).sub(orbitControls.target);
  if (_enterDir.lengthSq() < 1e-6) _enterDir.set(0, 1, 0);
  _enterDir.normalize();
  // Distance from the plan's zoom: the plan shows ~orthoHalfHeight/zoom metres
  // vertically; the perspective view covers ~dist·tan(fov/2), so match them.
  const planHalfH = orthoHalfHeight / Math.max(orthoCamera.zoom, 1e-3);
  const dist = THREE.MathUtils.clamp(
    planHalfH / Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)),
    1.5,
    100,
  );
  camera.position.copy(orbitControls.target).addScaledVector(_enterDir, dist);
  camera.up.copy(orthoCamera.up); // same on-screen orientation as the plan
  camera.lookAt(orbitControls.target);
  orbitControls.minDistance = 0.5;
  orbitControls.maxDistance = 120;
  orbitControls.update();
}

// Switching 3D → plan: snap to the orthographic view looking down whichever
// world axis the 3D camera is closest to facing from — the same axis snap model
// mode does when you turn ortho on (nearestAxisIn over the axis set). So a 3D
// view with +Z toward you lands on the +Z elevation, one looking mostly down
// lands on the bird's-eye top view, etc. Pan (target) and zoom (from the 3D
// distance) carry over.
const _enterUp = new THREE.Vector3();
function enter2DFromOrbit(): void {
  _planAlignActive = false; // set the camera directly, no tween
  // Direction from the orbit pivot toward the 3D camera = the axis "pointing at
  // you"; snap it to the nearest plan axis (the plan omits the −Y underside).
  _enterDir.copy(camera.position).sub(orbitControls.target);
  const dist3D = _enterDir.length();
  if (dist3D < 1e-4) _enterDir.set(0, 1, 0);
  _enterDir.normalize();
  const axis = nearestAxisIn(_enterDir, PLAN_AXES);
  planUpForAxis(axis, _enterUp); // canonical up for that axis (roll reset)
  floor2DControls.target.copy(orbitControls.target);
  orthoCamera.up.copy(_enterUp);
  orthoCamera.position
    .copy(floor2DControls.target)
    .addScaledVector(axis, PLAN_VIEW_DIST);
  // Zoom matched to the 3D distance (perspective half-height ≈ dist·tan(fov/2)).
  const planHalfH = Math.max(
    Math.max(dist3D, 1e-3) * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)),
    0.5,
  );
  orthoHalfHeight = 8;
  orthoCamera.zoom = THREE.MathUtils.clamp(orthoHalfHeight / planHalfH, 0.05, 50);
  sizeOrthoCamera();
  orthoCamera.lookAt(floor2DControls.target);
  floor2DControls.update();
}

// Assigned once the dock is installed (below); null-safe so the earlier
// syncToolbars() calls during setup don't trip over it.
let modelDock: CollectionDock | null = null;
function syncToolbars(): void {
  // Called on every mode/session transition — including VR session end, which
  // produces no browser input event, so the render loop must be woken here.
  wakeRendering();
  const vr = renderer.xr.isPresenting;
  desktopUI.setVisible(!vr && uiState.mode === "modeling");
  floorUI.setVisible(!vr && uiState.mode === "floorplan");
  modelDock?.setVisible(!vr); // bottom dock: desktop only, both modes
  modelDock?.setMode(uiState.mode);
  // Top-left cluster (VR edit / walkthrough): desktop only, hidden in XR. The
  // account menu (#dt-auth) is plain overlay DOM, hidden by the browser in XR.
  const corner = document.getElementById("corner-controls");
  if (corner) corner.style.display = vr ? "none" : "";
  // The VR edit + walkthrough buttons are floor-mode only now; Model mode keeps
  // just the dimensions readout (#dt-canvas-size), which shares this cluster.
  const showVrButtons = !vr && uiState.mode === "floorplan";
  const vrBtn = document.getElementById("vr-button");
  const walkBtn = document.getElementById("walkthrough-button");
  if (vrBtn) vrBtn.style.display = showVrButtons ? "" : "none";
  if (walkBtn) walkBtn.style.display = showVrButtons ? "" : "none";
}

const GIZMO_NEG_Y = new THREE.Vector3(0, -1, 0);
// One-shot: the first time the (blank, unsaved) default model canvas is shown,
// we prompt for its dimensions. Later blank entries leave sizing to Set
// dimensions / New, so switching modes back and forth doesn't keep nagging.
let modelDimsPrompted = false;

function setMode(m: "modeling" | "floorplan"): void {
  // A pending duplicate is already committed in the grid; leaving just drops its
  // (now irrelevant) non-destructive stash.
  clearSelFloat();
  setState("mode", m);
  const floor = m === "floorplan";
  gridGroup.visible = !floor;
  floorScene.root.visible = floor;
  // Layout never views the floor from below — hide the gizmo's −Y ball. Modeling
  // is 3D sculpting and keeps all six axes.
  setGizmoHiddenAxis(floor ? GIZMO_NEG_Y : null);
  cancelFloorGesture(); // drop any in-flight drag/marquee/polyline preview
  closeDimEdit(); // dismiss any open in-place dimension editor on a mode switch
  selectFloorVertex(null); // and any corner-lock padlock
  clearMarquee();
  if (floor) {
    wallSystem.setSelected(null);
    placementSystem.setSelected(null);
    measureSystem.finish();
    // A blank canvas means a new layout → start on the Wall tool so the user
    // can draw straight away; an in-progress layout keeps Select.
    setFloorTool(wallSystem.serialize().length === 0 ? "wall" : "select");
    refreshFloorSelectionUI();
    floorUI.render();
  } else {
    // Entering Model mode always starts on the Draw tool with the Paint action.
    setTopMode("sketch");
    setActionMode("draw");
  }
  syncToolbars();
  if (floor) frameFloorView();
  else {
    frameWorkVolume();
    // Re-entering modeling with ortho still on: snap the framed view to the
    // nearest axis so orthographic stays axis-aligned.
    if (modelOrthographic) setModelOrthographic(true);
  }
  // First arrival on the default model page → ask for canvas dimensions before
  // the user starts drawing (only while it's a blank, never-loaded canvas).
  if (!floor && !modelDimsPrompted) {
    modelDimsPrompted = true;
    if (grid.entries().next().done && modelSession.currentName() === "") {
      dimensionsPanel.open(currentDimsInitial());
    }
  }
}

function setFloorView(v: "2d" | "3d"): void {
  const from = uiState.floorView;
  setState("floorView", v);
  // The view toggle is a hotkey, so it can land mid-drag — cancel any
  // in-flight gesture rather than letting it straddle the camera swap.
  cancelFloorGesture();
  if (v === "3d") selectFloorVertex(null); // corners are 2D-only → drop any padlock
  floorVisibility.applyAll(); // 2D always shows walls; 3D honors the Walls toggle
  floorUI.render();
  // Toggling between the two views carries the camera over (pan/orientation/
  // zoom) in both directions; entering the mode fresh still reframes.
  if (v === "3d" && from === "2d") enter3DFromPlan();
  else if (v === "2d" && from === "3d") enter2DFromOrbit();
  else frameFloorView();
}

// Per-frame floor-mode camera update.
function updateFloorMode(): void {
  if (uiState.floorView === "2d") {
    floor2DControls.enabled = true;
    orbitControls.enabled = false;
    floor2DControls.update();
  } else {
    orbitControls.enabled = true;
    orbitControls.clampGround = true; // floor 3D: stay above the floor, upright
    orbitControls.minElev = (3 * Math.PI) / 180; // sliver above the horizon
    orbitControls.maxElev = (89 * Math.PI) / 180; // near-overhead
    orbitControls.rotateEnabled = true; // free orbit (not the model's axis-snap)
    floor2DControls.enabled = false;
    orbitControls.update();
  }
}


// ── Floor-plan mode (extracted to src/floor/floor-mode.ts) ────────────────
// Owns the floor systems (walls/placements/measure), tool gestures, marquee,
// in-place dimension editing, plan save/load, and the floor undo stack. The
// late-bound entries (floorUI/floorSession/modelDock) are created further down,
// so they're passed as thunks.
const floorMode = installFloorMode({
  scene,
  renderer,
  palette,
  orbitControls,
  orthoCamera,
  floor2DControls,
  desktopMarqueeRect,
  activeCamera,
  frameFloorView,
  pickFile,
  wakeRendering,
  sections: floorSections, // floor slices register sidebar sections here
  refreshFloorSaveUI: () => refreshFloorSaveUI(),
  wallsOn: () => floorVisibility.isOn("walls"),
  wallDimsOn: () => floorVisibility.isOn("wall-dims"),
  modelDimsOn: () => floorVisibility.isOn("model-dims"),
  emitLifecycle: lifecycle.emit,
  floorUI: () => floorUI,
  floorSession: () => floorSession,
  modelDock: () => modelDock,
});
const {
  floorScene,
  wallSystem,
  placementSystem,
  measureSystem,
  wallLabels,
  placementOutline,
  setFloorTool,
  getFloorTool,
  refreshFloorSelectionUI,
  finishDraw,
  closeDimEdit,
  selectFloorVertex,
  clearMarquee,
  cancelFloorGesture,
  clearFloorSelection,
  marqueeHasSelection,
  deleteMarquee,
  deleteFloorSelected,
  deleteSelectedOpening,
  flipSelectedDoor,
  hasSelectedDoor,
  setAllOpeningDimsVisible,
  deleteSelectedMeasurement,
  duplicateSelectedOpening,
  duplicateFloorSelected,
  floorUndoAction,
  floorRedoAction,
  resetFloorHistory,
  markFloorClean,
  floorDirty,
  buildCurrentFloorPlan,
  loadFloorPlan,
  newLayout,
  resetLayout,
  importLayoutFile,
  setFloorRotation,
  setFloorScale,
  addFloorOpening,
  setRoomHeight,
  updateMeasureTotalUI,
  placeModel,
  placeModelAt,
  onPresentationAO,
  cachePlacedModel,
  updateFloorFrame,
  frontLayerOn,
  toggleFrontLayer,
  isShiftHeld,
  setShiftHeld,
} = floorMode;

// Presentation rig furniture owned by the floor scene (ground plane, sun,
// dot-field swap) and the wall system's roof follow the lighting rig. The
// roof is solid in the walkthrough (you stand under it) but a camera-
// invisible shadow caster in the desktop preview, so the editor still looks
// into rooms from above while sunlight only enters through openings.
onLightingRig((on) => {
  floorScene.setPresentation(on);
  wallSystem.setCeilingMode(!on ? "off" : uiState.walkthroughActive ? "solid" : "ghost");
  placementSystem.setPresentation(on); // placed models swap to their AO look
  onPresentationAO(on); // ensure each placed model's AO is baked (on-demand)
});

// ── VR/XR layer (extracted to src/app/vr-input.ts) ────────────────────────
// Session lifecycle, the per-controller pick loop, panel layout/summon, the
// dial + face-button polls, the grab bar, and the layout walkthrough. The
// pick-result arrays are shared by reference with the desktop input path.
const vrInput = installVRInput({
  renderer,
  scene,
  camera,
  world,
  gridGroup,
  immersiveSkybox,
  floorScene,
  wallSystem,
  lastPick,
  lastPanelHit,
  controllerRays,
  resizeHotHandle,
  resizeHotDist,
  resizeHandleMeshes,
  volumeBounds,
  workVolumeSurfaces,
  DESKTOP_BACKGROUND,
  VR_GRID_POSITION,
  VR_GRID_ROTATION_X,
  DESKTOP_GRID_POSITION,
  DESKTOP_GRID_ROTATION_X,
  vrGridScale,
  syncToolbars,
  frameWorkVolume,
  frameFloorView,
  // Deferred: main's `layoutBounds` const is destructured from the desktop-input
  // install further down, so reference it lazily to dodge the TDZ at this literal.
  layoutBounds: () => layoutBounds(),
  setActionMode,
  setShapeMode,
  setSelectAction,
  modelUndo,
  modelRedo,
  grabbedHandleIndex,
  gestureRaycastMasks,
  multiObjectPick,
  dominantControllerIndex,
  nonDominantControllerIndex,
  settingsOpen: () => settingsPanelOpen,
  helpOpen: () => helpPanelOpen,
  txnOwner: () => activeTxnOwner,
});
const {
  walkthroughOn,
  layoutTabletopOn,
  updateWalkthrough,
  updatePickAndVisuals,
  pollFaceButtons,
  pollUndoRedo,
  pollDials,
  updateUIPanelsLayout,
  updateGrabBar,
  summonPanelAtGaze,
  clearPanelSummon,
  resetGrabBarPosition,
} = vrInput;

// ── Desktop UI shell (DOM toolbar) ─────────────────────────────────────────
// Desktop tools map 1:1 to TopModes (sketch == the "draw" tool).
function modeToDesktopTool(m: TopMode): DesktopTool {
  return m === "sketch" ? "draw" : (m as DesktopTool);
}
// Exact real dimensions (inches) the current canvas was set to, if known — so a
// saved model carries them and imports at the precise size. Null until set.
// (Declared here, before the readout/UI that reads it, to avoid a TDZ at the
// install-time render.)
let canvasRealIn: { w: number; d: number; h: number } | null = null;

// Whole-model snapshot for undoable canvas resizing — the grid cells plus the
// canvas bounds + voxel scale + entered size. Resizing prunes voxels and may
// recenter (shift) everything, so a per-cell diff is impractical; undo/redo just
// restore the captured state. Snapshots are taken before/after a resize and
// stashed in the history (see onSnapshotApply below).
interface ModelSnapshot {
  // Raw cloned chunk data — a fast whole-grid snapshot (see VoxelGrid.snapshot/
  // restoreChunks). Replaces the old per-cell entries() list, which was slow to
  // capture for a full grid (the source of the Fill/Clear commit lag).
  chunks: Map<number, ChunkStorage>;
  bounds: VolumeBounds;
  ipc: number;
  unit: LengthUnit;
  realIn: { w: number; d: number; h: number } | null;
}
function captureModelSnapshot(): ModelSnapshot {
  return {
    chunks: grid.snapshotChunks(),
    bounds: { ...volumeBounds },
    ipc: getInchesPerCell(),
    unit: getDisplayUnit(),
    realIn: canvasRealIn ? { ...canvasRealIn } : null,
  };
}
function applyModelSnapshot(snap: ModelSnapshot): void {
  grid.restoreChunks(snap.chunks);
  selection.replace([]); // cells may have moved/changed; old selection is stale
  volumeBounds.minX = snap.bounds.minX;
  volumeBounds.maxX = snap.bounds.maxX;
  volumeBounds.minZ = snap.bounds.minZ;
  volumeBounds.maxZ = snap.bounds.maxZ;
  volumeBounds.height = snap.bounds.height;
  recomputeCellBounds();
  setVoxelScale(snap.ipc, snap.unit);
  canvasRealIn = snap.realIn ? { ...snap.realIn } : null;
  rebuildWorkVolumeWalls();
  referenceImage.reanchor(volumeBounds); // keep a reference photo on its face
  desktopUIRef?.render();
}
// A bounded undo step (the extrude bulk paths) carries only the chunks it
// touched — restore just those, leaving the rest of the grid intact. A whole
// ModelSnapshot (other bulk ops) restores chunks + bounds.
onSnapshotApply((s) => {
  const sub = (s as { subsetChunks?: Map<number, ChunkStorage | null> }).subsetChunks;
  if (sub) {
    grid.restoreChunksSubset(sub);
    selection.replace([]);
    desktopUIRef?.render();
  } else {
    applyModelSnapshot(s as ModelSnapshot);
  }
});
// Fill/Clear, the box commits, and the extrude drag record one whole-grid
// snapshot for undo (so the drag itself writes no per-cell history).
setCanvasSnapshotCapture(captureModelSnapshot);
setBoxSnapshotCapture(captureModelSnapshot);
setExtrudeSnapshotCapture(captureModelSnapshot);
setCircleSnapshotCapture(captureModelSnapshot);
// Undo/redo of a swatch recolor restores the color table (in lockstep with the
// re-index deltas applied on the same diff) and repaints meshers + swatches.
onPaletteApply((colors) => {
  applyPaletteSnapshot(colors);
  refreshPaletteColors();
});

const sameBounds = (a: VolumeBounds, b: VolumeBounds): boolean =>
  a.minX === b.minX &&
  a.maxX === b.maxX &&
  a.minZ === b.minZ &&
  a.maxZ === b.maxZ &&
  a.height === b.height;

// (The per-gesture resize undo snapshot lives in tools/resize now.)

// Current canvas size for the sidebar readout: voxel cell counts (D×W×H) and
// the real-world size. "Real" prefers the exact stored dimensions (canvasRealIn,
// what was entered/saved); if those are unknown it falls back to the voxel-grid
// size × the voxel scale. Order is D × W × H (Z, X, Y) to match the dims panel.
function currentCanvasDims(): { vox: string; real: string } {
  const cx = Math.max(1, Math.round((volumeBounds.maxX - volumeBounds.minX) / CELL_SIZE));
  const cz = Math.max(1, Math.round((volumeBounds.maxZ - volumeBounds.minZ) / CELL_SIZE));
  const cy = Math.max(1, Math.round(volumeBounds.height / CELL_SIZE));
  const unit = getDisplayUnit();
  const f = (inches: number) => formatLengthIn(inches, unit);
  const ipc = getInchesPerCell();
  const real = canvasRealIn
    ? `${f(canvasRealIn.d)} × ${f(canvasRealIn.w)} × ${f(canvasRealIn.h)}`
    : `${f(cz * ipc)} × ${f(cx * ipc)} × ${f(cy * ipc)}`;
  return { vox: `${cz} × ${cx} × ${cy}`, real };
}

// Current canvas values to prefill the dimensions panel with, so it opens showing
// the model being edited (its real size + voxel size) rather than the last input.
function currentDimsInitial(): DimensionsInitial {
  const ipc = getInchesPerCell();
  const cx = Math.max(1, Math.round((volumeBounds.maxX - volumeBounds.minX) / CELL_SIZE));
  const cz = Math.max(1, Math.round((volumeBounds.maxZ - volumeBounds.minZ) / CELL_SIZE));
  const cy = Math.max(1, Math.round(volumeBounds.height / CELL_SIZE));
  const real = canvasRealIn ?? { w: cx * ipc, d: cz * ipc, h: cy * ipc };
  return {
    unit: getDisplayUnit(),
    widthIn: real.w,
    depthIn: real.d,
    heightIn: real.h,
    voxelIn: ipc,
  };
}

function currentDesktopUIState(): DesktopUIState {
  return {
    tool: modeToDesktopTool(uiState.topMode),
    action: uiState.actionMode,
    shape: uiState.shapeMode,
    symmetry: uiState.symmetry,
    gridOn: uiState.gridLinesVisible,
    edgesOn: uiState.edgesVisible,
    orthoOn: modelOrthographic,
    paletteIndex: uiState.currentPaletteIndex,
    dims: currentCanvasDims(),
    referenceOn: referenceOn(),
    referenceEditing: referenceEditingOn(),
    referenceHidden: referenceHiddenOn(),
    referenceOpacity: referenceOpacityValue(),
    frontLayerOn: frontLayerOn(),
  };
}
const _paletteCss: string[] = [];
for (let i = 0; i <= PALETTE_SIZE; i++) _paletteCss.push(paletteColorCss(i));

// After the palette table is edited at runtime, push the new colors into every
// scene object's cached materials (no re-mesh needed — materials are shared)
// and keep the desktop UI's CSS cache in sync. The VR palette panel and the
// desktop swatches both re-read live colors when they repaint.
function refreshPaletteColors(): void {
  for (const o of sceneObjects) o.mesher.refreshPaletteColors();
  raymarchView?.refreshPalette(); // keep the raymarch palette texture in lockstep
  for (let i = 0; i <= PALETTE_SIZE; i++) _paletteCss[i] = paletteColorCss(i);
  desktopUIRef?.refreshSwatches(); // repaint the swatch row (palette may be wholesale-swapped on load)
  wakeRendering();
}

const desktopUI = installDesktopUI({
  getState: currentDesktopUIState,
  paletteCss: _paletteCss,
  handlers: {
    // Tools / grid / edges reuse the toolkit-button routing (no VR panel
    // side effects for these ids).
    setTool: (t) => handlePanelClick({ panel: "toolkit", button: t }),
    toggleGrid: () => handlePanelClick({ panel: "toolkit", button: "grid" }),
    toggleEdges: () => handlePanelClick({ panel: "toolkit", button: "edges" }),
    toggleProjection: () => {
      setModelOrthographic(!modelOrthographic);
    },
    setAction: (a) => setActionMode(a),
    setShape: (s) => setShapeMode(s),
    toggleSymmetry: (axis) => {
      uiState.symmetry[axis] = !uiState.symmetry[axis];
    },
    rotateSelection: (axis, deg) =>
      rotateSelection(axis === "x" ? 0 : axis === "y" ? 1 : 2, deg),
    flipSelection: (axis) =>
      flipSelection(axis === "x" ? 0 : axis === "y" ? 1 : 2),
    invertSelection: () => invertSelection(),
    toggleFrontLayer,
    setPalette: (i) => {
      setState("currentPaletteIndex", i);
    },
    // Recolor a swatch WITHOUT touching voxels already painted with it: existing
    // voxels are frozen to a kept-color slot, the swatch takes the new color for
    // future painting, and the whole thing is one undoable step (which also marks
    // the model dirty via the transaction commit).
    editPalette: (i, hex) => {
      recolorSwatch(i, parseColorInt(hex));
      refreshPaletteColors();
    },
    resetPalette: () => {
      resetSwatches();
      refreshPaletteColors();
    },
    // Arm the voxel-color eyedropper; the next voxel click (sampled in
    // tryEyedropSample) calls back with its exact "#rrggbb". Waking the render
    // loop keeps the hover pick (lastPick) live so a click right after arming
    // still has a fresh target.
    beginEyedropper: (onSample) => {
      armEyedropper(onSample);
      wakeRendering();
    },
    cancelEyedropper: () => disarmEyedropper(),
    fillCanvas: () => {
      fillCanvas();
      triggerFillShield();
    },
    clearCanvas: () => clearCanvas(),
    saveModel: () => void modelSession.save(),
    saveModelAs: () => void modelSession.saveAs(),
    newModel: () => newModel(),
    importModel: () => importModelFile(),
    recenter: () => recenterCanvas(),
    openDimensions: () => dimensionsPanel.open(currentDimsInitial()),
    enterFloorPlan: () => setMode("floorplan"),
    importReference: (file) => {
      // Read as a data URL (not an object URL) so the same string can be saved
      // with the model.
      const reader = new FileReader();
      reader.onload = () => {
        const src = reader.result as string;
        const img = new Image();
        img.onload = () => {
          // Place it on the side you're looking at = the axis pointing toward the
          // camera (nearest world axis to camera→target).
          const normal = nearestAxisIn(
            _refNormalTmp.copy(activeCamera().position).sub(orbitControls.target),
            MODEL_AXES,
          );
          importReferenceLoaded(img, src, normal);
        };
        img.src = src;
      };
      reader.readAsDataURL(file);
    },
    toggleReferenceEdit,
    toggleReferenceHidden,
    setReferenceOpacity,
  },
});
desktopUIRef = desktopUI;
// The account menu (top-right) + Settings/Help/Welcome are wired together lower
// down, once the desktop help + welcome handles exist (search installAccountMenu).

// ── Desktop input layer (extracted to src/app/desktop-input.ts) ───────────
// Mouse → synthetic controller 0, per-tool desktop drags, press/release
// routing, reference dragging, the corner nav gizmos, and view-align tweens.
// Close the freeform polygon lasso: arm mirror, then fill the enclosed area and
// select what was drawn, all inside ONE transaction (one undo step). Shared by
// the closing click (desktop-input) and the Enter shortcut.
function closeFreeformShape(): void {
  if (!freeformPolyActive()) return;
  setMirror(currentMirrorConfig()); // symmetric models fill symmetrically
  beginTransaction();
  const drawn = closeFreeformPoly(cellBounds, uiState.currentPaletteIndex);
  if (drawn && drawn.length > 0) selection.replace(drawn);
  endTransaction();
  wakeRendering();
}

const desktopInput = installDesktopInput({
  camera,
  orthoCamera,
  orbitControls,
  floor2DControls,
  gridGroup,
  grid,
  selection,
  cellBounds,
  referenceImage,
  lastPick,
  controllerRays,
  resizeHandleMeshes,
  resizeHotHandle,
  resizeHotDist,
  desktopMarqueeRect,
  activeObject,
  placementSystem,
  wallSystem,
  MODEL_AXES,
  PLAN_VIEW_DIST,
  desktopUI,
  activeCamera,
  gestureObject,
  gestureRaycastMasks,
  multiObjectPick,
  grabbedHandleIndex,
  mirrorExpandSelection,
  selectionStateString,
  commitReference,
  beginGridAction,
  finalizeInteraction,
  freeformResolveTarget: (i) => {
    // The freeform lasso's first vertex picks the object it draws into (a voxel
    // hit → that object; a room surface → the room-aligned object). Null when the
    // cursor is over nothing (handled by the caller — no polygon starts).
    const targetId = lastPickObject[i];
    const target =
      targetId === ROOM_TARGET ? getOrCreateRoomObject() : objectById(targetId);
    return target ? { grid: target.grid, group: target.group } : null;
  },
  closeFreeform: closeFreeformShape,
  applyResizeExtent,
  nearestAxisIn,
  planUpForAxis,
  snapUpForAxis,
  isShiftHeld,
  frontLayerOn,
  modelOrthoOn: () => modelOrthographic,
  setModelOrthoFlag: (on) => {
    modelOrthographic = on;
  },
  referenceEditingOn,
  planAlignOn: () => _planAlignActive,
  setPlanAlign: (on) => {
    _planAlignActive = on;
  },
  getSelFloat,
  clearSelFloat,
  tryEyedropSample,
});
const {
  updateDesktopPointer,
  updateDesktopPickAndCursor,
  updateDesktopMarquee,
  updateDesktopExtrude,
  updateDesktopBox,
  updateDesktopFreeform,
  updateFreeformPreview,
  updateDesktopResize,
  updateDesktopMove,
  updateGizmoDrag,
  updateViewTween,
  updateFloorNavGizmo,
  applyReferenceDrag,
  startViewAlign,
  startPlanViewAlign,
  setModelOrthographic,
  cancelViewAlign,
  layoutBounds,
  gizmoDrag,
  connectedObject,
} = desktopInput;

const floorUI = installFloorUI({
  sections: floorSections,
  getState: () => ({
    view: uiState.floorView,
    tool: getFloorTool(),
    unit: getDisplayUnit(),
    roomHeightText: formatLengthIn(wallSystem.height() * M_TO_IN, getDisplayUnit()),
    frontLayerOn: frontLayerOn(),
    lightingOn: uiState.presentationLighting,
  }),
  handlers: {
    setMode,
    setView: setFloorView,
    setTool: setFloorTool,
    setRotation: setFloorRotation,
    setScale: setFloorScale,
    addOpening: addFloorOpening,
    flipDoor: () => flipSelectedDoor(false), // mirrors the F hotkey
    switchDoorSides: () => flipSelectedDoor(true), // mirrors the Shift+F hotkey
    saveLayout: () => void floorSession.save(),
    saveLayoutAs: () => void floorSession.saveAs(),
    newLayout: () => newLayout(),
    importLayout: () => importLayoutFile(),
    // Switch the measurement display unit. Pills, the measure readout, and the
    // selected-wall/opening fields all read getDisplayUnit() when they format,
    // so reformat the on-demand ones; the pills refresh on the next sync.
    setUnit: (u) => {
      setDisplayUnit(u);
      saveSetting("displayUnit", u); // explicit choice → persists across reloads
      refreshFloorSelectionUI();
      updateMeasureTotalUI();
      measureSystem.refreshLabels(); // re-format existing measurement pills
      floorUI.render();
    },
    setRoomHeight: (text) => setRoomHeight(text),
    toggleFrontLayer,
    toggleLighting: () =>
      setState("presentationLighting", !uiState.presentationLighting),
    recenter: () => frameFloorView(),
  },
});
// Save/Save As/dirty session for the current layout. Save overwrites the entry
// being edited (silent, with a green flash); Save As always prompts and creates
// a new entry. Dirty is "the layout differs from the last save / load / blank".
const floorSession = createSaveSession<SavedFloorPlan>({
  noun: "layout",
  isEmpty: () =>
    wallSystem.serialize().length === 0 && placementSystem.serialize().length === 0,
  build: (name) => buildCurrentFloorPlan(name),
  idOf: (p) => p.id,
  nameOf: (p) => p.name,
  createdAtOf: (p) => p.createdAt,
  reidentify: (p, id, createdAt) => {
    p.id = id;
    p.createdAt = createdAt;
  },
  list: () => listPlans(),
  put: (p) => putPlan(p),
  onSaveStart: () => floorUI.flashSaved(), // flash on click, before build/persist
  afterSave: () => {
    modelDock?.refresh(); // reflect the new/updated layout in the collection drawer
    refreshFloorSaveUI();
  },
  isDirty: () => floorDirty(),
  markClean: () => markFloorClean(),
});
function refreshFloorSaveUI(): void {
  floorUI.setUnsaved(floorSession.hasUnsavedWork());
  floorUI.setName(floorSession.currentName());
}
syncToolbars();
resetFloorHistory(); // establish the initial (empty) layout baseline

// Canvas Dimensions tool: resize the work volume to an object's real W×D×H at
// the chosen voxel resolution, and set the voxel→real scale so Measure + the
// walls report the true proportions to draw within.
// Resize the work volume to the given real dimensions; existing voxels that
// still fit are kept, ones now outside the smaller canvas are clipped.
function applyDimensions(r: DimensionsResult): void {
  // Wrap in a transaction with a before/after snapshot so Set-dimensions is one
  // undo step (like the resize tool). New-model clears history right after, so
  // this entry is harmless there.
  const before = captureModelSnapshot();
  beginTransaction();
  // Capture the existing voxels + the OLD cell extents before the resize so we can
  // resample the model into the new grid: raising the voxel density should keep the
  // model at the same physical size with finer detail, not shrink it into a corner.
  // (applyVolumeBounds prunes voxels outside the new bounds, so grab them first.)
  const oldEntries = [...grid.entries()];
  const oldCellBounds = { ...cellBounds };
  const halfX = Math.floor(r.cellsX / 2) * CELL_SIZE;
  const halfZ = Math.floor(r.cellsZ / 2) * CELL_SIZE;
  applyVolumeBounds({
    minX: -halfX,
    maxX: -halfX + r.cellsX * CELL_SIZE,
    minZ: -halfZ,
    maxZ: -halfZ + r.cellsZ * CELL_SIZE,
    height: r.cellsY * CELL_SIZE,
  });
  // Remap the saved voxels onto the resized canvas (no-op when the cell extents
  // didn't change, e.g. only the real size changed at the same density).
  resampleGridToBounds(oldEntries, oldCellBounds, cellBounds);
  setVoxelScale(r.voxelInches, r.unit);
  canvasRealIn = { ...r.realIn };
  if (!renderer.xr.isPresenting) frameWorkVolume();
  // applyVolumeBounds already rendered the sidebar, but with the OLD scale +
  // canvasRealIn — the readout depends on both, so refresh once they're set.
  desktopUIRef?.render();
  const after = captureModelSnapshot();
  if (!sameBounds(before.bounds, after.bounds) || before.ipc !== after.ipc) {
    recordSnapshot(before, after);
  }
  endTransaction();
}
const dimensionsPanel = installDimensionsPanel({ onApply: applyDimensions });

// Model collection: the bottom dock browses saved models (click → load to edit
// in Model mode; drag → place in Layout mode); a separate Save dialog captures
// the current model (voxels + canvas dimensions + scale + a rendered thumbnail).
const thumbnailer = new Thumbnailer(renderer, palette);
// Undo-history depth at the last save / load / new. The model is "dirty" when
// the current depth differs — so undoing back to the saved point reads as clean
// (a sticky boolean wouldn't). The commit listener fires on edit, undo, AND
// redo, so the indicator refreshes after each. Loads/clears use suppressRecording
// + clearHistory, so the depth resets to 0 there.
let savedHistoryDepth = 0;
// A freshly-created (never-saved) model counts as unsaved even though its history
// is empty — set on New, cleared on save / load. Lets the save dot + the
// "save before leaving?" prompt treat a new model like the work-in-progress it is.
let modelForceDirty = false;
// The current model's last-known thumbnail. Save uses it as a placeholder so it
// doesn't re-mesh+render synchronously (the freeze for big models); the
// off-thread bake regenerates it from the baked geometry and writes it back.
// Set on load (the saved art), blanked on New, refreshed after each regen.
let lastThumbnail = "";
onTransactionCommit(() => {
  refreshModelSaveUI();
});
function buildCurrentModel(name: string): SavedModel {
  // A blank canvas is saveable too (it still carries dimensions + any reference);
  // an empty grid just serializes to no cells and a blank thumbnail.
  const now = Date.now();
  const ipc = getInchesPerCell();
  // Always record exact real dims: from the dimensions panel when set, else
  // derived from the current canvas (cells × inches/cell).
  const cellsX = Math.max(1, Math.round((volumeBounds.maxX - volumeBounds.minX) / CELL_SIZE));
  const cellsY = Math.max(1, Math.round(volumeBounds.height / CELL_SIZE));
  const cellsZ = Math.max(1, Math.round((volumeBounds.maxZ - volumeBounds.minZ) / CELL_SIZE));
  const realIn = canvasRealIn ?? { w: cellsX * ipc, d: cellsZ * ipc, h: cellsY * ipc };
  return {
    id: newId(),
    name,
    version: 2,
    // Compressed source voxels (flat RLE). No baked asset here — build is
    // synchronous/main-thread; the render asset is computed off-thread and
    // written back in afterSave (scheduleBake).
    voxels: gridToVoxels(grid),
    paletteColors: snapshotPalette(), // freeze this model's colors so swatch edits elsewhere never recolor it
    bounds: {
      minX: volumeBounds.minX,
      maxX: volumeBounds.maxX,
      minZ: volumeBounds.minZ,
      maxZ: volumeBounds.maxZ,
      height: volumeBounds.height,
    },
    scale: { inchesPerCell: ipc, unit: getDisplayUnit() },
    realIn: { ...realIn },
    // Placeholder — the prior thumbnail (or blank for a new model). scheduleBake
    // regenerates it off the critical path from the baked mesh.
    thumbnail: lastThumbnail,
    reference: serializeReference(),
    createdAt: now,
    updatedAt: now,
  };
}
function loadModel(m: SavedModel): void {
  wakeRendering(); // may land after a confirm dialog, past the idle window
  suppressRecording(() => {
    grid.clear();
    if (isV2(m)) {
      voxelsToGrid(m.voxels, grid);
      // setRawChunk bypasses dirty-tracking, so the renderers wouldn't see the
      // new chunks — dirty them all (grid.clear already bumped version).
      grid.markAllChunksDirty();
    } else {
      cellsToGrid(m.cells, grid);
    }
  });
  resetModelMeshers(); // same-dims loads: drop the previous model's meshes NOW
  // Restore the model's own palette (legacy files with none → factory defaults),
  // then push it into the meshers + swatch row. This is what keeps each saved
  // model's colors independent of swatch edits made while other models are open.
  applyPaletteSnapshot(m.paletteColors);
  refreshPaletteColors();
  selection.replace([]); // old selection points at cells that no longer exist
  applyVolumeBounds({ ...m.bounds });
  setVoxelScale(m.scale.inchesPerCell, m.scale.unit);
  clearHistory();
  canvasRealIn = m.realIn ? { ...m.realIn } : null;
  lastThumbnail = m.thumbnail ?? ""; // placeholder for the next save-over
  savedHistoryDepth = historyDepth(); // freshly loaded = clean baseline (depth 0)
  modelForceDirty = false; // a loaded model is saved, not dirty
  modelSession.setCurrent(m.id, m.name, m.createdAt); // Save now overwrites it
  refreshModelSaveUI();
  restoreReference(m.reference); // bring back the saved reference photo (or clear)
  desktopUIRef?.render(); // refresh size readout (bounds may be unchanged)
  // A loaded model opens in the 3D (perspective) view.
  if (modelOrthographic) setModelOrthographic(false);
  if (!renderer.xr.isPresenting) frameWorkVolume();
}
// After a save: bake the model's SHELL asset off the main thread (into the
// separate bake store), regenerate the thumbnail from it (deferred from save),
// and upgrade any existing placements. AO is NOT baked here — it's computed
// on-demand when presentation lighting turns on (floor onPresentationAO). The
// asset is an optional, regenerable cache, so a missing worker is harmless.
function scheduleBake(model: SavedModel): void {
  if (!isV2(model)) return;
  const id = model.id;
  const stamp = model.updatedAt;
  const busy = beginBusy("Optimizing model…");
  void ensureAsset(model, { force: true })
    .then(async (ok) => {
      // Regenerate the thumbnail from the (now-cached) shell geometry — no
      // re-mesh when the bake succeeded; cloneForModel falls back to a fast
      // shell build if the worker was unavailable.
      const thumbnail = thumbnailer.captureObject(cloneForModel(model, palette)) || lastThumbnail;
      // Write just the thumbnail back, and only if this is still the live record.
      const current = await getModel(id);
      if (current && isV2(current) && current.updatedAt === stamp) {
        if (thumbnail && thumbnail !== current.thumbnail) {
          try {
            await putModel({ ...current, thumbnail });
          } catch {
            /* best-effort thumbnail */
          }
        }
        lastThumbnail = thumbnail || lastThumbnail;
      }
      if (ok) {
        // Existing placements re-clone from the fast asset path now.
        clearClones();
        placementSystem.rebuildModel(id, () => cloneForModel(model, palette));
        placementOutline.refresh();
      }
      modelDock?.refresh(); // show the fresh thumbnail
      wakeRendering(); // landed outside an input event (idle-gate)
    })
    .finally(() => busy.done());
}

// One-time background migration so listModels() stays light: older records
// stored the baked asset INLINE and/or kept voxels as per-chunk objects (slow to
// structured-clone). Move any inline `baked` into the separate bake store, and
// flatten legacy `voxels.chunks` to the flat `voxels.data` array. One model at a
// time so it never freezes the UI. Best-effort + idempotent.
async function migrateModels(): Promise<void> {
  let ids: string[];
  try {
    ids = await listModelIds();
  } catch {
    return;
  }
  for (const id of ids) {
    const m = (await getModel(id).catch(() => undefined)) as
      | (SavedModel & { baked?: BakedAssetCore; cells?: number[]; voxels?: { chunks?: unknown } })
      | undefined;
    if (!m) continue;
    const hasBaked = !!m.baked;
    const isV1 = m.version !== 2 && Array.isArray(m.cells);
    const legacyVoxels = m.version === 2 && Array.isArray(m.voxels?.chunks);
    if (!hasBaked && !isV1 && !legacyVoxels) continue;
    const { baked, cells, ...rest } = m;
    const next = rest as SavedModel & { version: number; voxels?: unknown };
    if (isV1 && cells) {
      // Dense v1 cells → flat v2 voxels (the heavy field bloating listModels).
      next.version = 2;
      next.voxels = v1CellsToVoxels(cells);
    } else if (legacyVoxels) {
      next.voxels = flattenLegacyVoxels(m.voxels as { chunks: { key: number; rle: number[] }[] });
    }
    try {
      if (hasBaked && baked) {
        await putBake({
          id: m.id,
          updatedAt: m.updatedAt,
          shell: baked.shell,
          ao: baked.ao,
          cellBounds: baked.cellBounds,
        });
      }
      await putModel(next as SavedModel);
    } catch {
      /* leave it for next boot */
    }
  }
}

// Start a fresh model at the chosen dimensions: blank the canvas + reset state.
// Document swap (New / load): tear the meshers down SYNCHRONOUSLY and re-feed.
// The incremental sync path sends chunk teardowns through the mesh worker, so
// after a whole-grid clear the OLD model's meshes survive one worker roundtrip
// — if the mesh path is the visible renderer (zoomed in), the old model
// renders for a frame at the freshly framed camera: an "enlarged model" flash.
// (The dims-CHANGED case is also covered by the render loop's "all" reset;
// this handles same-dims swaps, where sync takes the incremental branch.)
function resetModelMeshers(): void {
  for (const o of sceneObjects) {
    o.mesher.reset();
    o.grid.markAllChunksDirty();
  }
}

function applyNewCanvas(r: DimensionsResult): void {
  suppressRecording(() => grid.clear());
  resetModelMeshers();
  selection.replace([]); // fresh canvas → drop any lingering selection
  applyDimensions(r);
  clearHistory();
  restoreReference(undefined); // a new model has no reference photo
  lastThumbnail = ""; // fresh model → no placeholder art until the first bake
  savedHistoryDepth = historyDepth(); // blank canvas = clean baseline (depth 0)
  modelSession.clearCurrent();
  refreshModelSaveUI();
}
// New-model flow: offer to save unsaved edits, then pick dimensions, then build
// the blank canvas (in Model mode).
// The app's blank starting canvas (the default work volume + 1 in/voxel), as a
// DimensionsResult so New can rebuild it.
function defaultDimsResult(): DimensionsResult {
  const cx = Math.round((DESKTOP_GRID_HALF_SIZE.x * 2) / CELL_SIZE);
  const cy = Math.round(VOLUME_HEIGHT / CELL_SIZE);
  const cz = Math.round((DESKTOP_GRID_HALF_SIZE.y * 2) / CELL_SIZE);
  const voxelInches = 1; // units._inchesPerCell starts at 1
  return {
    cellsX: cx,
    cellsY: cy,
    cellsZ: cz,
    voxelInches,
    unit: getDisplayUnit(),
    realIn: { w: cx * voxelInches, d: cz * voxelInches, h: cy * voxelInches },
  };
}

async function newModel(): Promise<void> {
  if ((await modelSession.confirmLeaveUnsaved()) === "cancel") return;
  if (uiState.mode !== "modeling") setMode("modeling");
  // A fresh model starts in the 3D (perspective) view — switch back from the 2D
  // orthographic view if that's where we were.
  if (modelOrthographic) setModelOrthographic(false);
  // Reset to a blank default canvas FIRST so the new model exists before the
  // prompt — escaping the dialog leaves you on it, not the model you were
  // editing. Apply then rebuilds with the entered size.
  applyNewCanvas(defaultDimsResult());
  // A fresh model starts from the factory palette — don't inherit the colors of
  // whatever document was open (those belong to that model, saved with it).
  applyPaletteSnapshot(undefined);
  refreshPaletteColors();
  // A new model is unsaved work: show the save dot + prompt before leaving it.
  // (applyNewCanvas reset the flag to clean, so set it after.)
  modelForceDirty = true;
  refreshModelSaveUI();
  dimensionsPanel.open(currentDimsInitial(), applyNewCanvas);
}

// Pop a one-off file picker, hand the chosen file to `cb`, then clean up.
function pickFile(accept: string, cb: (file: File) => void): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = accept;
  input.style.display = "none";
  input.addEventListener("change", () => {
    const f = input.files?.[0];
    input.remove();
    // The OS file dialog can sit open past the idle window with no page
    // input; the import's scene changes land after it closes, so wake here.
    wakeRendering();
    if (f) cb(f);
  });
  document.body.appendChild(input);
  input.click();
}

// Import a model .json into the collection, then refresh the dock. Models are
// keyed by id, so re-importing an updated version of an existing model
// overwrites its entry — and if that entry is the one currently open in the
// editor, the live grid would otherwise keep showing the stale voxels. Reload
// it (through the unsaved-changes guard) so the view reflects the imported data.
function importModelFile(): void {
  pickFile("application/json,.json", async (file) => {
    try {
      const imported = await readModelFile(file);
      await putModel(imported);
      modelDock?.refresh();
      if (imported.id === modelSession.currentId()) await loadModelGuarded(imported);
    } catch (e) {
      await showAlert((e as Error).message, { title: "Couldn't import model" });
    }
  });
}

modelDock = installCollectionDock({
  onLoadModel: (m) => void loadModelGuarded(m),
  onPlaceModelCenter: placeModel,
  onPlaceModelAt: placeModelAt,
  onLoadLayout: (p) => void loadLayoutGuarded(p),
  canvas: renderer.domElement,
});
// When a sign-in pulls the account's models/layouts down and merges them into
// local storage, re-list the collection drawer so they appear without a reload.
syncController.onChange(() => modelDock?.refresh());
// Save/Save As/dirty session for the current model — the modeling-mode twin of
// floorSession. Save overwrites the entry being edited; Save As always prompts.
// The "Saving…" busy pill, held across onSaveStart → afterSave.
let saveBusy: ReturnType<typeof beginBusy> | null = null;
const modelSession = createSaveSession<SavedModel>({
  noun: "model",
  isEmpty: () => false, // a canvas (even blank) is always saveable
  build: (name) => buildCurrentModel(name),
  idOf: (m) => m.id,
  nameOf: (m) => m.name,
  createdAtOf: (m) => m.createdAt,
  reidentify: (m, id, createdAt) => {
    m.id = id;
    m.createdAt = createdAt;
  },
  list: () => listModels(),
  put: (m) => putModel(m),
  onSaveStart: () => {
    // A non-blocking "Saving…" pill (delayed-show, so fast saves don't flash it).
    saveBusy?.done();
    saveBusy = beginBusy("Saving…");
    desktopUI.flashSaved(); // flash on click, before build/persist
  },
  afterSave: (model) => {
    clearClones(); // a save-over changed the geometry → rebuild placement clones
    // Push the edits into any existing layout placements of this model. The
    // just-saved model has no asset yet, so this uses the fast shell-only build;
    // scheduleBake upgrades it once the off-thread shell bake lands. (Transform
    // — position/yaw/scale — is preserved.)
    cachePlacedModel(model); // so undo restores the latest geometry
    placementSystem.rebuildModel(model.id, () => cloneForModel(model, palette));
    placementOutline.refresh(); // the selected placement's mesh was swapped
    modelDock?.refresh();
    refreshModelSaveUI();
    scheduleBake(model); // bake the shell asset off-thread + regen the thumbnail
    saveBusy?.done();
    saveBusy = null;
  },
  isDirty: () => modelForceDirty || historyDepth() !== savedHistoryDepth,
  markClean: () => {
    savedHistoryDepth = historyDepth();
    modelForceDirty = false;
  },
});
function refreshModelSaveUI(): void {
  desktopUI.setUnsaved(modelSession.hasUnsavedWork());
  desktopUI.setName(modelSession.currentName());
}
refreshModelSaveUI(); // initialise the model save indicator + name

// ── Account scope ────────────────────────────────────────────────────────────
// Signing in/out swaps the whole local library (per-account pools — see
// sync/account-scope). The open documents belong to the pool being left, so:
// interactive auth actions first offer to save unsaved work (a Save here still
// writes into the OLD pool — the guard runs before the switch)…
setScopeSwitchGuard(async () => {
  if ((await modelSession.confirmLeaveUnsaved()) === "cancel") return false;
  if ((await floorSession.confirmLeaveUnsaved()) === "cancel") return false;
  return true;
});
// …then the scope change resets both documents to blank and re-lists the dock
// for the new pool. Fires on boot too when a persisted session restores (the
// documents are still blank then, so the reset is a no-op visually).
onScopeChange(() => {
  applyNewCanvas(defaultDimsResult());
  applyPaletteSnapshot(undefined); // fresh doc → factory palette, like New
  refreshPaletteColors();
  modelForceDirty = false; // a scope swap opens on a clean blank, not "unsaved"
  refreshModelSaveUI();
  resetLayout();
  modelDock?.refresh();
  wakeRendering(); // auth callbacks arrive without a browser input event
});

// Loading another document from the collection dock replaces the current one of
// that type, so offer to save its unsaved changes first (same flow as New).
async function loadModelGuarded(m: SavedModel): Promise<void> {
  if ((await modelSession.confirmLeaveUnsaved()) === "cancel") return;
  loadModel(m);
}
async function loadLayoutGuarded(p: SavedFloorPlan): Promise<void> {
  if ((await floorSession.confirmLeaveUnsaved()) === "cancel") return;
  await loadFloorPlan(p);
  wakeRendering(); // may land after a confirm dialog, past the idle window
}
// Start in Layout (floor-planning) mode — applies scene visibility, toolbar
// state, the default Wall tool, and the floor-view framing in one call.
// Restore persisted preferences before the boot mode switch, so the first
// frame already shows the user's unit / hand / view-toggle choices. setState
// routes through the same subscriptions as a live change (meshers, persist).
{
  const saved = loadSettings();
  if (saved.displayUnit) setDisplayUnit(saved.displayUnit);
  if (saved.dominantHand) setState("dominantHand", saved.dominantHand);
  if (saved.gridLinesVisible !== undefined) {
    setState("gridLinesVisible", saved.gridLinesVisible);
  }
  if (saved.edgesVisible !== undefined) setState("edgesVisible", saved.edgesVisible);
}
setMode("floorplan");

// The boot document. A brand-new browser gets the bundled starter scene seeded
// and opened; every boot after that reopens the layout edited last. Nothing to
// open (a pool that only ever held models, say) leaves the blank layout.
//
// Opening in a fitted 3D view is the default treatment for BOTH cases, so the
// app always opens looking at a room. setFloorView first so the visibility
// rules + sidebar toggle follow, THEN frameFloorView for the canonical fitted
// 3/4 — setFloorView alone would carry the plan camera's straight-down axis
// into 3D.
async function openBootLayout(): Promise<void> {
  const boot = await pickBootLayout();
  if (!boot) return;
  // Prime the in-session model cache BEFORE loading, so a freshly seeded scene
  // renders even when the models couldn't be written to the store.
  for (const m of boot.models) cachePlacedModel(m);
  await loadFloorPlan(boot.plan);
  modelDock?.refresh(); // seeded/pulled models are new to the drawer
  setFloorView("3d");
  frameFloorView();
  wakeRendering(); // landed outside any input event (idle gate)
}

// ONLY when a session might restore, wait for auth to settle before opening
// anything. A restoring session swaps the scope asynchronously and fires
// onScopeChange → resetLayout(), so opening first would paint a scene and then
// blank it — the flash. Waiting means we open exactly once, against the final
// scope's pool.
//
// Signed-out boots skip the wait entirely and open immediately: they can't
// change scope, and making them wait would put the cost of loading the auth
// client (seconds) in front of every anonymous user's first paint.
//
// The wait is raced against a timeout so a hung auth/pull can never leave the
// app with no document at all.
const BOOT_AUTH_SETTLE_TIMEOUT_MS = 15000;
void (async () => {
  if (hasPersistedSession()) {
    await Promise.race([
      authSettled(),
      new Promise<void>((res) => setTimeout(res, BOOT_AUTH_SETTLE_TIMEOUT_MS)),
    ]);
  }
  await openBootLayout();
})();

// Background, one-time: relocate legacy inline bakes to the bake store + flatten
// legacy voxels, so model listing stays light. Non-blocking; one model at a time.
void migrateModels();

function runDragHandlers(): void {
  const gObj = gestureObject();
  handleExtrudeDrag(gObj.grid, gObj.group, cellBounds);
  handleBoxDrag(lastPick, gObj.grid, cellBounds, uiState.currentPaletteIndex);
  handleCircleDrag(lastPick, gObj.grid, cellBounds, uiState.currentPaletteIndex);
  handleVoxelPaintDrag(
    lastPick,
    gObj.grid,
    cellBounds,
    uiState.currentPaletteIndex,
  );
  handleMarqueeDrag();
  handleMoveDrag(grid, gridGroup, cellBounds, selection);
  handleResizeDrag(gridGroup, applyResizeExtent);
}



// ── Render Loop ──────────────────────────────────────────────────────────

// ── AppContext ───────────────────────────────────────────────────────────
// The shared services + registries every subsystem slice installs against (see
// ARCHITECTURE.md). The frame-step / keymap registries are created here; the
// keymap's commands are still registered further below.
const frameLoop = createFrameLoop(); // per-frame step registry (slots, e.g. "desktopPre")

// Zoom-fade the per-voxel grid + edge lines (MagicaVoxel look: lines crisp up
// close, easing away as the model gets small so colors stay clean). We measure a
// voxel's on-screen size from the orbit-target distance — the same half-height
// the ortho camera is sized to (syncModelOrthoCamera), so ONE formula covers
// both model projections.
//
// The lines are ~1px wide at ANY zoom, so as voxels shrink their share of each
// face's screen AREA grows. A plain opacity ramp fights this badly: rising
// coverage makes the (white) lines read brighter as you zoom out even while
// opacity falls, and a hard floor lets coverage keep climbing at fixed opacity —
// the result is a non-monotonic brightness (up, then down, then up) instead of a
// clean fade. So instead we COMPENSATE for coverage: estimate the lines'
// screen-area fraction and cap how much of a face they may tint. Perceived line
// intensity (opacity × coverage) then rises smoothly to the cap and holds —
// monotonic, crisp when zoomed in, a faint even tint at max zoom-out.
const LINE_TINT_CAP = 0.25; // grid + edge: max fraction of a face they may tint
const LINE_PX_WIDTH = 2; // approx on-screen line footprint in px (both axes)
frameLoop.register("desktopPre", () => {
  if (uiState.mode !== "modeling") {
    setAllLineFade(1, 1); // idempotent; restores full lines outside model mode
    return;
  }
  const dist = Math.max(1e-3, camera.position.distanceTo(orbitControls.target));
  const halfH = dist * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
  const pxPerVoxel = (CELL_SIZE * window.innerHeight * 0.5) / halfH;
  // Coverage = fraction of a face's area the ~1px lines occupy at this zoom;
  // grows as voxels shrink. Opacity = the cap divided by coverage (≤1), so
  // perceived intensity (opacity × coverage) holds at the cap once lines get
  // dense instead of climbing — a smooth, monotonic fade. Full opacity while
  // zoomed in (coverage below the cap → lines crisp).
  const coverage = Math.min(1, LINE_PX_WIDTH / pxPerVoxel);
  const fade = Math.min(1, LINE_TINT_CAP / Math.max(coverage, 1e-3));
  setAllLineFade(fade, fade); // gridlines + edges
  boxPreview.setLineFade(fade, fade); // keep the box-draw shell matching
  raymarchView?.setLineFade(fade, fade); // and the raymarch grid/edge lines
});

const keymap = createKeymap(); // keyboard command registry (commands registered below)
const tools = createToolRegistry(); // gesture tools (consulted by applyToolAction)
const app: AppContext = {
  scene,
  renderer,
  camera,
  orthoCamera,
  orbitControls,
  floor2DControls,
  activeCamera,
  wakeRendering,
  isPresenting: () => renderer.xr.isPresenting,
  frameLoop,
  keymap,
  lifecycle,
  tools,
};

// WASD + E/Q camera movement (3D perspective views). Self-contained in
// app/camera-move; it registers its mover into the non-VR "desktopPre" slot.
installCameraMove(app);

// Desktop onboarding + help (ui/welcome + ui/desktop-help), then the top-right
// account menu that hosts Settings / Controls & shortcuts / Show welcome intro
// alongside the account items. The menu always mounts; account items appear
// only when a Supabase backend is configured.
const desktopHelp = installDesktopHelp(app);
const welcome = installWelcome({ openHelp: desktopHelp.open });
installAccountMenu({
  openSettings,
  openHelp: desktopHelp.open,
  showWelcome: welcome.show,
});

// Voxel clipboard slice (copy/paste/duplicate + pending placement). Registers
// its own Cmd/Ctrl C/V/D shortcuts; main injects the bits it still owns.
const clipboard = installClipboard({
  keymap,
  isModelMode: () => uiState.mode === "modeling",
  cellBounds, // mutated in place by the resize tool — same live object
  txnOwned: () => activeTxnOwner !== null,
});

// Gesture-tool slices (tools/). Each registers its trigger-press start + its
// interaction-kind commits; applyToolAction / finalizeInteraction consult the
// registry first and fall back to the legacy branches for unmigrated tools.
installMoveTool({
  tools,
  pickFor: (i) => lastPick[i],
  gridGroup,
  connectedObject,
  mirrorExpandSelection,
  mirrorConfigNow: () => (mirrorActiveNow() ? currentMirrorConfig() : null),
  screenDriven: () => !renderer.xr.isPresenting,
  clipboard,
});
installSelectTool({
  tools,
  pickFor: (i) => lastPick[i],
  gridGroup,
  mirrorConfigNow: () => (mirrorActiveNow() ? currentMirrorConfig() : null),
  screenDriven: () => !renderer.xr.isPresenting,
  clipboard,
  headCamera: () => (renderer.xr.isPresenting ? renderer.xr.getCamera() : camera),
  emptySpaceHitDist: (i) => {
    controllers[i].getWorldPosition(_pickOriginTmp);
    grabBarMesh.getWorldPosition(_grabBarWorldPosTmp);
    return _pickOriginTmp.distanceTo(_grabBarWorldPosTmp);
  },
  marqueeRect,
});
installResizeTool({
  tools,
  gridGroup,
  volumeBounds, // live object — the drag mutates it in place
  hotHandle: (i) => resizeHotHandle[i],
  hotDist: (i) => resizeHotDist[i],
  surfaceAt: (idx) => workVolumeSurfaces[idx],
  axisDriven: () => !renderer.xr.isPresenting,
  captureSnapshot: captureModelSnapshot,
  commitSnapshotIfChanged: (before) => {
    const b = before as ModelSnapshot;
    const after = captureModelSnapshot();
    if (!sameBounds(b.bounds, after.bounds)) recordSnapshot(b, after);
  },
  recenterCanvasBounds,
});
installSketchTool({
  tools,
  pickFor: (i) => lastPick[i],
  resolveGestureTarget: (i) => {
    // A voxel hit → that object; a room-surface hit → the room-aligned object
    // (created on demand). Marked as the gesture target so the per-frame drag
    // handlers + commit route to this object's grid/frame until release.
    const targetId = lastPickObject[i];
    const target =
      targetId === ROOM_TARGET ? getOrCreateRoomObject() : objectById(targetId);
    setGestureObject(target.id);
    return { grid: target.grid, group: target.group };
  },
  gestureGrid: () => gestureObject().grid,
  cellBounds, // live object — mutated in place by the resize tool
  mirrorConfig: currentMirrorConfig,
  screenDriven: () => !renderer.xr.isPresenting,
});

// PAINT tool — recolor existing voxels with the same five shapes as Sketch.
// Shares Sketch's gesture-target resolution; differs only in claiming
// topMode "paint" and recoloring instead of placing.
installPaintTool({
  tools,
  pickFor: (i) => lastPick[i],
  resolveGestureTarget: (i) => {
    const targetId = lastPickObject[i];
    const target =
      targetId === ROOM_TARGET ? getOrCreateRoomObject() : objectById(targetId);
    setGestureObject(target.id);
    return { grid: target.grid, group: target.group };
  },
  cellBounds,
  mirrorConfig: currentMirrorConfig,
  screenDriven: () => !renderer.xr.isPresenting,
});

installBucketTool({
  tools,
  pickFor: (i) => lastPick[i],
  resolveGestureTarget: (i) => {
    const targetId = lastPickObject[i];
    const target =
      targetId === ROOM_TARGET ? getOrCreateRoomObject() : objectById(targetId);
    setGestureObject(target.id);
    return { grid: target.grid };
  },
  cellBounds,
  globalFill: () => isShiftHeld(),
});

const _vrHeadPosTmp = new THREE.Vector3();

renderer.setAnimationLoop(() => {
  if (
    !renderer.xr.isPresenting &&
    performance.now() - _lastActivity > IDLE_RENDER_WINDOW_MS
  ) {
    return; // idle: skip update + render; the canvas keeps its last frame
  }
  if (!renderer.xr.isPresenting) frameLoop.run("desktopPre"); // e.g. camera movement
  // Reference overlay is desktop-Model-mode + ortho only; the modeling branch
  // turns it back on when appropriate. Off by default each frame (VR/floorplan).
  referenceImage.group.visible = false;
  if (renderer.xr.isPresenting && walkthroughOn()) {
    // VR layout walkthrough: only teleport locomotion runs; the modeling tools,
    // panels, and grab-bar are all dormant.
    updateWalkthrough();
    orbitControls.enabled = false;
  } else if (renderer.xr.isPresenting && layoutTabletopOn()) {
    // AR layout tabletop: a passthrough diorama on the desk. The live controls
    // are the dual-palm-grip canvas gesture (rotate / zoom / pan the layout) and
    // the see-through walls — no modeling tools, panels, grab bar, or locomotion.
    // The nav context was pointed at floorScene.root on entry.
    handleNavigation();
    // See-through walls, tracking head motion: fade whichever wall pixels stand
    // between the headset and the room interior (same effect as the desktop 3D
    // view). updateOcclusionFade runs in wall-local space, so the tabletop's
    // scaled / positioned / rotated layout is handled without extra bookkeeping.
    if (wallSystem.isVisible()) {
      renderer.xr.getCamera().getWorldPosition(_vrHeadPosTmp);
      wallSystem.updateOcclusionFade(_vrHeadPosTmp.x, _vrHeadPosTmp.y, _vrHeadPosTmp.z);
    } else {
      wallSystem.clearOcclusionFade();
    }
    orbitControls.enabled = false;
  } else if (renderer.xr.isPresenting) {
    pollFaceButtons();
    pollUndoRedo();
    pollDials();
    updateUIPanelsLayout();
    updateGrabBar();
    handleCanvasDrag();
    handleNavigation();
    runDragHandlers();
    updatePickAndVisuals();
    updateResizeHandlesVisual();
    orbitControls.enabled = false;
  } else if (uiState.mode === "floorplan") {
    // FLOOR-PLANNING (desktop): camera + wall/placement tools + gizmo.
    updateFloorMode();
    // The dot "island" follows the layout: clear around it, fading into the dark
    // beyond (a radius past the layout's extent, not relative to the camera).
    const ib = layoutBounds();
    if (ib) {
      const icx = (ib.min.x + ib.max.x) / 2;
      const icz = (ib.min.z + ib.max.z) / 2;
      const ir = 0.5 * Math.hypot(ib.max.x - ib.min.x, ib.max.z - ib.min.z);
      floorScene.setGridIsland(icx, icz, ir + 12, ir + 30);
    } else {
      floorScene.setGridIsland(0, 0, 14, 32); // empty layout → island at origin
    }
    // Corner nav gizmo (3D view) gets first crack at the press; it skips the
    // floor tools while it owns the gesture (orbit drag / view-align click).
    const navActive = updateFloorNavGizmo();
    updateFloorFrame(navActive);
  } else {
    // DESKTOP (keyboard + mouse). Orbit on RMB / pan on MMB / zoom on
    // scroll (handled inside Orbit3D); LMB drives the tool via a synthetic
    // "controller 0" fed by the mouse ray.
    orbitControls.enabled = true;
    // Modeling: gimbal-locked turntable — yaw is free (360°) but pitch is clamped
    // just shy of the poles and the up stays +Y, so you can orbit to look from
    // above or below but can never tumble past the Y axis to flip the model
    // upside down. Exact top/bottom views are still reachable via axis-snap.
    orbitControls.clampGround = true;
    orbitControls.minElev = (-89 * Math.PI) / 180; // look up from below
    orbitControls.maxElev = (89 * Math.PI) / 180; // look down from above
    // Orthographic modeling is axis-snapped: free RMB orbit off (the axis-snap
    // swipe handles right-drag); perspective keeps the normal free orbit.
    orbitControls.rotateEnabled = !modelOrthographic;
    floor2DControls.enabled = false;
    updateViewTween(); // advance an in-progress gizmo view-align first
    orbitControls.update(); // keep the camera aimed at the target
    if (modelOrthographic) syncModelOrthoCamera(); // slave ortho cam → persp
    const refMode = updateReferenceVisibility();
    // View controls (pointer routing, nav gizmo orbit/axis-align) ALWAYS run so
    // they keep working in reference-edit mode; only the voxel tools are paused.
    updateDesktopPointer();
    updateGizmoDrag();
    if (refMode) {
      applyReferenceDrag(); // LMB transforms the image (press/release in onDesktop*)
    } else {
      runDragHandlers();
      updateDesktopBox();
      updateDesktopFreeform();
      updateFreeformPreview();
      updateDesktopExtrude();
      updateDesktopResize();
      updateDesktopMove();
      updateDesktopMarquee();
      updateDesktopPickAndCursor();
      updateResizeHandlesVisual();
    }
    // No hover highlight while dragging the gizmo to orbit, or while swinging
    // the camera with a right-drag orbit / middle-drag pan.
    const showHover =
      desktopMouse.overCanvas &&
      !desktopMouse.camDragging &&
      !(gizmoDrag.active && gizmoDrag.moved);
    setGizmoHover(
      showHover ? desktopMouse.clientX : -1,
      showHover ? desktopMouse.clientY : -1,
    );
  }

  // Marquee rect preview tracks the in-flight gesture; hides otherwise.
  if (interaction.kind === "marquee_select") {
    marqueeRect.update(interaction);
  } else {
    marqueeRect.hide();
  }

  // Re-mesh dirty chunks under a per-frame budget so a volume-sized edit (commit
  // a big box fill, Fill/Clear the canvas) rebuilds over several frames instead
  // of blocking for seconds. Keep the loop awake while any chunk is still
  // pending so the rest finishes even without further input.
  let meshPending = false;
  // Raymarch is the active renderer only for desktop Model-mode editing — and
  // only while zoomed out enough that its per-covered-pixel cost fits the
  // frame budget (see raymarchZoomedOut above). VR, Layout, presentation
  // lighting, and close zoom fall back to the mesh path.
  if (raymarchView && uiState.mode === "modeling" && !renderer.xr.isPresenting) {
    // Apparent size of one voxel at the nearest point of the work volume, in
    // CSS px. Local-space distance is fine for perspective (the scale cancels
    // against the local cell size); ortho reads the frustum height instead.
    camera.getWorldPosition(_rmCamLocalTmp);
    gridGroup.worldToLocal(_rmCamLocalTmp);
    _rmClampTmp.set(
      Math.min(Math.max(_rmCamLocalTmp.x, volumeBounds.minX), volumeBounds.maxX),
      Math.min(Math.max(_rmCamLocalTmp.y, 0), volumeBounds.height),
      Math.min(Math.max(_rmCamLocalTmp.z, volumeBounds.minZ), volumeBounds.maxZ),
    );
    const dist = Math.max(_rmCamLocalTmp.distanceTo(_rmClampTmp), 1e-3);
    const vh = renderer.domElement.clientHeight || 1;
    const cellPx = modelOrthographic
      ? (CELL_SIZE * gridGroup.scale.x * vh) /
        Math.max(modelOrthoCamera.top - modelOrthoCamera.bottom, 1e-6)
      : (CELL_SIZE * vh) / (2 * Math.tan((camera.fov * Math.PI) / 360) * dist);
    // Freeze the choice during a gesture: box/extrude live-draw vs preview-
    // shell is picked per renderer, and swapping one mid-drag would hand the
    // gesture to the other path's machinery. Entering the mesh path also
    // waits for the mesher to catch up (meshCatchupPending, last frame's
    // sync) — zooming in right after a big raymarch-side edit must not show
    // a half-rebuilt model while worker chunks are still arriving. The other
    // direction needs no gate: the volume texture is re-synced every frame.
    if (interaction.kind === "idle") {
      if (
        raymarchZoomedOut &&
        cellPx > RAYMARCH_MESH_AT_CELL_PX &&
        !meshCatchupPending
      ) {
        raymarchZoomedOut = false;
      } else if (!raymarchZoomedOut && cellPx < RAYMARCH_RESUME_CELL_PX) {
        raymarchZoomedOut = true;
      }
    }
  }
  const useRaymarch =
    raymarchView !== null &&
    uiState.mode === "modeling" &&
    !renderer.xr.isPresenting &&
    !uiState.presentationLighting &&
    (FORCE_RAYMARCH || raymarchZoomedOut);
  if (raymarchView) raymarchView.mesh.visible = useRaymarch;
  for (const o of sceneObjects) o.mesher.object3D.visible = !useRaymarch;
  // The raymarcher paints the selection into its own surface (white gridlines on
  // selected cells), so the overlay line mesh — which z-fights the raymarched
  // depth — is hidden while raymarching and only drives the mesh path.
  selectionOutline.object3D.visible = !useRaymarch;
  // Keep the work-volume cage (the white canvas-bounds wireframe) visible. The
  // raymarcher writes true hit-surface depth (gl_FragDepth), so the cage is
  // depth-tested normally — occluded by the voxels, visible at the open bounds.
  cage.visible = true;
  // With raymarch, box/extrude DRAW write the fill live (cheap) instead of a
  // preview shell — so the cube + growing edit are one renderer.
  setBoxLiveDraw(useRaymarch);
  setExtrudeLiveDraw(useRaymarch);
  // BOTH representations stay warm every frame: the volume texture keeps
  // uploading dirty chunks while the mesh renders, and the mesher keeps
  // meshing while the raymarcher renders (its meshes just stay hidden). That
  // makes the zoom swap above a pure visibility flip — no reset, no
  // markAllChunksDirty rebuild wave, no one-renderer-went-stale transition.
  // The old dormant-mesher reset lived exactly here; always-sync retires it.
  // The grid's dirty-chunk queue is one-shot, so whatever the volume sync
  // drains is handed straight on to the active object's mesher ("all" = the
  // volume full-rebuilt, e.g. a resize — re-feed the mesher everything too).
  let rmConsumed: Set<number> | "all" | null = null;
  if (raymarchView && uiState.mode === "modeling" && !renderer.xr.isPresenting) {
    rmConsumed = raymarchView.syncVolume(activeObject.grid, cellBounds);
    raymarchView.syncSelection(selection); // mirror selection AFTER the volume sync
    raymarchView.updateCamera(activeCamera());
  }
  if (rmConsumed === "all") {
    // Full volume rebuild (new/loaded document, work-volume resize): the
    // dirty set syncVolume just discarded may have named now-DELETED chunks
    // (grid.clear dirties them so meshes tear down), and markAllChunksDirty
    // can only re-mark chunks that still exist. Reset the mesher's worker
    // mirror so vanished chunks drop with it — without this, New Model after
    // a load kept the old model's meshes, visible whenever the mesh path
    // rendered (zoomed in).
    activeObject.mesher.reset();
    activeObject.grid.markAllChunksDirty();
  }
  for (const o of sceneObjects) {
    const extra = o === activeObject && rmConsumed !== "all" ? rmConsumed : null;
    if (o.mesher.sync(o.grid, MESH_MS_BUDGET, extra ?? undefined)) meshPending = true;
  }
  meshCatchupPending = meshPending;
  if (meshPending) wakeRendering();

  // Preview shield: a live box_draw drag keeps the shell tracking the box; a
  // committed box (idle) or a Fill holds the last shell over the building mesh.
  // While held over a building mesh, the real mesh's lines are suppressed so the
  // shell's lines don't double them (a dark grid sweeping across the region).
  const releaseShield = (): void => {
    boxPreview.hide();
    fillShield = null;
    if (realLinesSuppressed) {
      setAllGridVisible(uiState.gridLinesVisible);
      setAllEdgesVisible(uiState.edgesVisible);
      realLinesSuppressed = false;
    }
  };
  // box_erase carves live (no preview) — the revealed cavity is the feedback.
  // box_draw and rectangular extrude_draw show a growing-box shell + commit on
  // release (mesh path). With raymarch they write the fill live, so no shell is
  // needed — the model + the growing edit are one renderer.
  if (useRaymarch) {
    releaseShield();
  } else {
    if (interaction.kind === "box_draw") {
      const s = interaction.startCell;
      const c = interaction.currentCell;
      setFillShield(s.ix, s.iy, s.iz, c.ix, c.iy, c.iz, uiState.currentPaletteIndex);
    } else if (interaction.kind === "extrude_draw") {
      const db = extrudeDraftBox(); // null for mask/non-preview extrudes (live)
      if (db) setFillShield(db.a.ix, db.a.iy, db.a.iz, db.b.ix, db.b.iy, db.b.iz, db.palette);
    }
    if (fillShield) {
      boxPreview.update(fillShield.a, fillShield.b, fillShield.color, cellBounds);
      if (interaction.kind !== "box_draw" && interaction.kind !== "extrude_draw") {
        if (meshPending) {
          // Holding over the building mesh — hide its lines behind the shell's.
          if (!realLinesSuppressed) {
            setAllGridVisible(false);
            setAllEdgesVisible(false);
            realLinesSuppressed = true;
          }
        } else {
          releaseShield(); // mesh finished (or never started) → drop the shield
        }
      }
    } else {
      releaseShield();
    }
  }

  const cam = activeCamera();
  // The selection tracks the cells as they move (setMoveOffset keeps it in
  // sync), so the outline stays on the voxels throughout the drag. The camera +
  // canvas width let it fade its white edges when voxels shrink on screen.
  selectionOutline.sync(selection, grid, cam, renderer.domElement.clientWidth);
  updateWorkVolumeVisibility();

  renderer.render(scene, cam);

  // Corner orientation gizmo — drawn for every desktop view. It mirrors the
  // camera actually on screen: the perspective camera for modeling (the model
  // ortho cam is synced to it) and the floor 3D view, or the plan's ortho
  // camera in the 2D view (where clicking an axis snaps to that orthographic
  // view). Skipped only in XR.
  if (!renderer.xr.isPresenting) {
    renderGizmoOverlay(renderer, cam === orthoCamera ? orthoCamera : camera);
  }
});

window.addEventListener("resize", () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  applyDesktopViewOffset(); // recompute the sidebar shift + projection matrix
  sizeOrthoCamera(); // floor 2D plan camera
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// Closing/reloading the tab with unsaved work gets the browser's "leave site?"
// prompt — the same dirty checks that drive the save dot and the
// confirm-on-switch dialogs. (Browsers show their own generic message.)
window.addEventListener("beforeunload", (e) => {
  if (modelSession.hasUnsavedWork() || floorSession.hasUnsavedWork()) {
    e.preventDefault();
  }
});

// Desktop keyboard shortcuts, as a data-driven command registry (app/keymap):
// commands match in registration order, first match wins — a faithful, 1:1
// translation of the old if/else chain. Each `run` owns its own preventDefault
// (some shortcuts deliberately fall through to the browser default); a command
// that matches but no-ops still consumes the event. New shortcuts register here
// (or from a subsystem module) instead of growing one central handler.
// (The keymap registry itself is created above, with the AppContext.)
{
  const cmd = (e: KeyboardEvent) => e.ctrlKey || e.metaKey; // Cmd/Ctrl combo
  const bare = (e: KeyboardEvent) => !e.ctrlKey && !e.metaKey && !e.altKey;
  const floor = (): boolean => uiState.mode === "floorplan";
  const model = (): boolean => uiState.mode === "modeling";

  // ── Global (either mode) ──
  keymap.register({
    id: "save",
    when: (e) => cmd(e) && (e.key === "s" || e.key === "S"),
    run: (e) => {
      e.preventDefault();
      const session = uiState.mode === "floorplan" ? floorSession : modelSession;
      if (e.shiftKey) void session.saveAs();
      else void session.save();
    },
  });
  // 2D / 3D view toggle (rebindable; default "5") — before the mode split so it
  // works in both modes.
  keymap.register({
    id: "view-toggle",
    when: (e) =>
      bare(e) &&
      actionForKey(e.key.toLowerCase(), floor() ? "layout" : "model") === "view-toggle",
    run: (e) => {
      e.preventDefault();
      toggle2D3D();
    },
  });

  // ── Freeform polygon lasso (Model mode, while an outline is being built) ──
  // Registered before the mode shortcuts so they win over the generic
  // Escape/Backspace while building; all gate on freeformPolyActive().
  keymap.register({
    id: "freeform-close",
    when: (e) => model() && freeformPolyActive() && e.key === "Enter",
    run: (e) => {
      e.preventDefault();
      closeFreeformShape();
    },
  });
  keymap.register({
    id: "freeform-cancel",
    when: (e) => model() && freeformPolyActive() && e.key === "Escape",
    run: (e) => {
      e.preventDefault();
      cancelFreeformPoly();
      wakeRendering();
    },
  });
  keymap.register({
    id: "freeform-pop", // remove the last-placed vertex
    when: (e) =>
      model() &&
      freeformPolyActive() &&
      (e.key === "Backspace" || e.key === "Delete"),
    run: (e) => {
      e.preventDefault();
      popFreeformVertex();
      wakeRendering();
    },
  });

  // ── Floor-plan mode ──
  keymap.register({
    id: "floor-escape", // finish a wall polyline + the measure chain, drop selection
    when: (e) => floor() && e.key === "Escape",
    run: () => {
      finishDraw();
      measureSystem.finish();
      clearFloorSelection();
    },
  });
  keymap.register({
    id: "floor-delete", // opening → measurement → marquee → selected wall/placement
    when: (e) => floor() && (e.key === "Delete" || e.key === "Backspace"),
    run: (e) => {
      e.preventDefault();
      if (!deleteSelectedOpening() && !deleteSelectedMeasurement()) {
        if (marqueeHasSelection()) deleteMarquee();
        else deleteFloorSelected();
      }
    },
  });
  keymap.register({
    id: "floor-duplicate",
    when: (e) => floor() && cmd(e) && (e.key === "d" || e.key === "D"),
    run: (e) => {
      e.preventDefault();
      if (!duplicateSelectedOpening()) duplicateFloorSelected();
    },
  });
  keymap.register({
    id: "floor-undo",
    when: (e) => floor() && cmd(e) && (e.key === "z" || e.key === "Z"),
    run: (e) => {
      e.preventDefault();
      if (e.shiftKey) floorRedoAction();
      else floorUndoAction();
    },
  });
  keymap.register({
    id: "floor-redo",
    when: (e) => floor() && cmd(e) && (e.key === "y" || e.key === "Y"),
    run: (e) => {
      e.preventDefault();
      floorRedoAction();
    },
  });
  keymap.register({
    id: "floor-door-flip", // F flips a selected door's hinge; Shift+F its swing.
    // Gated on a door being selected so an un-door 'f' falls through to the tool
    // hotkeys (first-match-wins: this only claims the key when it can act).
    when: (e) => floor() && !cmd(e) && (e.key === "f" || e.key === "F") && hasSelectedDoor(),
    run: (e) => {
      e.preventDefault();
      flipSelectedDoor(e.shiftKey);
    },
  });
  keymap.register({
    id: "floor-tool-hotkey", // Select / Wall / Measure (rebindable, hotkeys.ts)
    when: (e) => {
      if (!floor() || !bare(e)) return false;
      const a = actionForKey(e.key.toLowerCase(), "layout");
      return !!(a && FLOOR_HOTKEY_ACTIONS.has(a));
    },
    run: (e) => {
      e.preventDefault();
      HOTKEY_ACTIONS[actionForKey(e.key.toLowerCase(), "layout")!]();
      floorUI.render();
    },
  });

  // ── Model mode ──
  keymap.register({
    id: "model-tool-hotkey", // Tools + Shape keys (rebindable); bare keys only
    when: (e) => {
      if (!model() || !bare(e)) return false;
      const a = actionForKey(e.key.toLowerCase(), "model");
      return !!(a && HOTKEY_ACTIONS[a]);
    },
    run: (e) => {
      e.preventDefault();
      HOTKEY_ACTIONS[actionForKey(e.key.toLowerCase(), "model")!]();
      desktopUI.render();
    },
  });
  keymap.register({
    id: "model-escape", // cancel the voxel selection (recorded so undo restores it)
    when: (e) => model() && e.key === "Escape" && selection.size() > 0,
    run: (e) => {
      e.preventDefault();
      clearSelFloat();
      const before = selectionStateString();
      selection.replace([]);
      commitSelectionChange(before, "");
    },
  });
  keymap.register({
    id: "model-delete-reference", // editing the reference image → remove it
    when: (e) => model() && (e.key === "Delete" || e.key === "Backspace") && editingReference(),
    run: (e) => {
      e.preventDefault();
      clearReferenceImage();
    },
  });
  keymap.register({
    id: "model-delete-voxels",
    when: (e) => model() && (e.key === "Delete" || e.key === "Backspace") && selection.size() > 0,
    run: (e) => {
      e.preventDefault();
      deleteSelection();
    },
  });
  // (model-copy / model-paste / model-duplicate are registered by the
  // core/edit-core/clipboard slice at install.)
  keymap.register({
    id: "model-undo",
    when: (e) => model() && cmd(e) && (e.key === "z" || e.key === "Z"),
    run: (e) => {
      e.preventDefault();
      if (e.shiftKey) modelRedo();
      else modelUndo();
    },
  });
  keymap.register({
    id: "model-redo",
    when: (e) => model() && cmd(e) && (e.key === "y" || e.key === "Y"),
    run: (e) => {
      e.preventDefault();
      modelRedo();
    },
  });
}

window.addEventListener("keydown", (e) => {
  if (e.key === "Shift") setShiftHeld(true); // wall-snap override (any mode)
  if (renderer.xr.isPresenting) return;
  if (
    e.target instanceof HTMLInputElement ||
    e.target instanceof HTMLTextAreaElement
  ) {
    return; // don't hijack typing (future DOM UI inputs)
  }
  keymap.dispatch(e);
});

window.addEventListener("keyup", (e) => {
  if (e.key === "Shift") setShiftHeld(false);
});
